import { Controller } from "@hotwired/stimulus"

// Curvature timeline ("Corner geometry") for the Admin::Tracks workbench.
//
// Phase D: the curvature waveform + distance/sector/start-finish axis + wheel
// zoom + pan scrollbar + scrub emission. Corner spans/handles (Phase E), the Fit
// marquee, and the docked geometry strip (Phase F) are NOT drawn here.
//
// Ported from the designer prototype `design/cw-timeline.jsx`. The prototype uses
// the OPPOSITE curvature sign convention to this app:
//   prototype:  k<0 = right, k>0 = left
//   app (Telemetry::ComputeCurvature):  k>0 = right, k<0 = left
// We reconcile with a SINGLE negation at ingestion — display curvature `kd = -k`
// — then implement the waveform EXACTLY as the prototype does, treating `kd` as
// the prototype's `k`. Net effect (the required visual):
//   • an app LEFT corner has raw k<0 → kd>0 → yOf(kd>0) is ABOVE baseline,
//     and WaveStroke's `kd>=0 ? "L" : "R"` test picks "L" → blue (#00D4FF). ✔
//   • an app RIGHT corner has raw k>0 → kd<0 → BELOW baseline → "R" → orange. ✔
// `maxK` uses |raw k| (negation can't change magnitude).
//
// Data values on the element (set in admin/tracks/_track_detail.html.erb):
//   data-corner-timeline-curvature-value          — [[distance_m, k], …] (signed k, downsampled)
//   data-corner-timeline-corners-geometry-value   — [{number,name,start_m,apex_m,end_m,direction,source}, …]
//   data-corner-timeline-sectors-value            — [boundary_m, …] (metres) or []
//   data-corner-timeline-length-value             — track length (metres)
//   data-corner-timeline-rotation-value           — unused here
//
// Public API (mirrors track_map_hero_controller's view contract so Phase G can
// bi-sync map ↔ timeline):
//   setView({a,b} | null)   — push a distance window (null/full-span = whole lap);
//                             re-renders WITHOUT re-emitting viewChange (no loop).
// Emitted events:
//   corner-timeline:viewChange  detail { view: {a,b} }  — on USER zoom/pan
//   corner-timeline:scrub       detail { m }            — pointer over waveform
//   corner-timeline:scrub       detail { m: null }      — pointer leaves

const LEFT_HUE = "#00D4FF" // cool — left-hand curvature (app k<0 → kd>0)
const RIGHT_HUE = "#FF7A1C" // warm — right-hand curvature (app k>0 → kd<0)
const WAVE_H = 150 // waveform band height (constant; zoom is horizontal only)
const MIN_SPAN = 140 // minimum visible distance window, metres

// ── Section band layer (Phase C) ─────────────────────────────────────────────
//
// Sections TILE the whole lap: contiguous ranges, section[i].end_m ==
// section[i+1].start_m. Editing happens on the shared BOUNDARY between two
// neighbours (one shrinks, the other grows). These mirror `design/cw-sections.js`
// (`window.CWS`) for LIVE drag preview; the SERVER (TrackSections::MoveBoundary /
// SplitAt) is the source of truth on commit.

// Minimum section length in metres — a boundary can't push a neighbour below it.
// Ported from cw-sections.js (`const MIN = 60`) + TrackSections::MoveBoundary::MIN.
const SECTION_MIN = 60

// Swappable band palette — distinct hues at one chroma/lightness. MUST stay in
// sync with TracksHelper::SECTION_BAND_PALETTE (Ruby) + cw-sections.js PALETTE.
const SECTION_PALETTE = [
  "#5B8DEF",
  "#8B5BEF",
  "#C44FD0",
  "#E0598B",
  "#E08A4F",
  "#C9A227",
  "#5BBF73",
  "#3FB6B6",
  "#6E7BE0",
  "#B069D8",
  "#D86E9E",
  "#E0A14F",
  "#7FB84F",
  "#4FB89C",
]
const sectionColorFor = (i) =>
  SECTION_PALETTE[
    ((i % SECTION_PALETTE.length) + SECTION_PALETTE.length) %
      SECTION_PALETTE.length
  ]

// Corners whose APEX falls inside [start_m, end_m]. (cw-sections.js containedCorners)
function containedCorners(section, corners) {
  return (Array.isArray(corners) ? corners : []).filter(
    (c) => c.apex_m >= section.start_m && c.apex_m <= section.end_m,
  )
}

// Move the shared boundary BETWEEN section i and i+1 to distance m (clamped so
// neither neighbour drops below SECTION_MIN). Returns a NEW array; both touched
// sections get end_m/start_m set + source "manual". (cw-sections.js moveBoundary)
function moveBoundary(sections, i, m) {
  if (i < 0 || i >= sections.length - 1) return sections
  const lo = sections[i].start_m + SECTION_MIN
  const hi = sections[i + 1].end_m - SECTION_MIN
  const v = Math.round(Math.max(lo, Math.min(hi, m)))
  return sections.map((s, k) =>
    k === i
      ? { ...s, end_m: v, source: "manual" }
      : k === i + 1
        ? { ...s, start_m: v, source: "manual" }
        : s,
  )
}

function sectionIndexAt(sections, m) {
  for (let i = 0; i < sections.length; i++) {
    if (m >= sections[i].start_m && m <= sections[i].end_m) return i
  }
  return -1
}

const sectionDisplayName = (s) =>
  s.name && String(s.name).trim()
    ? String(s.name).trim()
    : `Section ${s.number}`

function svgEl(tag, attrs = {}) {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag)
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
  return el
}

// Nice axis step ≈ span/8, snapped to a 1/2/2.5/5/10 × 10^n grid. (cw-timeline.jsx)
function niceStep(spanM) {
  const target = spanM / 8
  const pow = Math.pow(10, Math.floor(Math.log10(target)))
  const cand = [1, 2, 2.5, 5, 10].map((m) => m * pow)
  return cand.find((c) => c >= target) || cand[cand.length - 1]
}

export default class extends Controller {
  static targets = [
    "host",
    "strip",
    "removeDialog",
    "removeBody",
    "removeConfirm",
    "mergeDialog",
    "mergeBody",
  ]
  static values = {
    curvature: Array,
    cornersGeometry: Array,
    sectors: Array,
    length: Number,
    rotation: Number,
    geometryUrlTemplate: String,
    magicUrlTemplate: String,
    removeUrlTemplate: String,
    fitUrl: String,
    // Section endpoints (Phase C). `__ID__` is replaced with a TrackSection id.
    boundaryUrlTemplate: String, // PATCH …/sections/__ID__/boundary  {index, m}
    splitUrl: String, // POST  …/sections/split             {at_m}
    mergeUrlTemplate: String, // POST  …/sections/__ID__/merge       {dir}
  }

  connect() {
    const L = this.lengthValue || 0
    this._L = L
    // ALWAYS (re)derive the view from length — it depends only on this element's
    // own value, never on a parent push, so it's safe to set unconditionally and
    // is exactly the state a pre-connect render was missing.
    this._view = { a: 0, b: L }
    // Preserve a mode the parent may have pushed via setMode() BEFORE this
    // connect() ran (the connect-order race). Default to corners on a true
    // first connect.
    this._mode ??= "corners"

    // Span/handle interaction state (Phase E).
    this._selectedNumber = null // corner number selected (lit + apex readout)
    this._hoveredNumber = null // corner number hovered (active styling)
    this._drag = null // active handle/rail drag, or null

    // Fit marquee state (Phase F1).
    this._fitScope = null // { start_m, end_m } scoped range, or null
    this._fitCount = 2 // 1 | 2 — corners to fit (default 2: Ferradura)
    this._scopeDrag = null // active scope/marquee drag, or null

    // Geometry strip state (Phase F2). The strip DOM is built ONCE per selection
    // change and updated in place on subsequent renders, so a focused <input> is
    // never torn down mid-type and live handle-drag → field updates stay smooth.
    // `_stripKey` is the selection identity the current strip DOM was built for
    // (corner number + db id), so we only rebuild when that identity changes.
    this._stripKey = undefined
    this._stripFields = null // { start, apex, end } → { input, stepUp, stepDn }
    this._stripLength = null // the Length readout span
    this._stripDirBtns = null // { left, right } direction toggle buttons
    this._pendingRemoveId = null // corner id the remove dialog is confirming

    // ── Section layer state (Phase C) ────────────────────────────────────────
    // Stable working copy of the sections tiling, seeded from the corner-curation
    // parent via setSections(). Mirrors `_corners`: a single persistent reference
    // we mutate during a boundary drag (a Stimulus value would re-parse on every
    // read). Section bands + the docked section strip render off this. `??=`
    // preserves a tiling the parent pushed via setSections() BEFORE this connect()
    // ran (the connect-order race) — an unconditional `= []` would wipe it.
    this._sections ??= []
    this._selectedSectionId ??= null // selected section id (band lit + strip shown)
    this._hoveredSectionId ??= null // section id glowing from hover (NOT selection)
    this._boundaryDrag = null // active boundary drag, or null
    this._activeBoundary = null // hovered/dragged BOUNDARY index (distinct from band hover)
    // Section-strip render identity (focus-safe in-place update, like the corner
    // geometry strip): the strip DOM is rebuilt only when this key changes.
    this._secStripKey = undefined
    this._secStripFields = null // { start, end } → { input }
    this._secStripLength = null // the Length readout span
    this._pendingMergeId = null // section id the merge dialog is confirming
    this._pendingMergeDir = -1 // direction the pending merge collapses toward
    // Stable working copy of the corners geometry. Stimulus Array/Object value
    // GETTERS RE-PARSE the JSON attribute on EVERY access, so each read returns a
    // brand-new object graph — mutating one read is invisible to the next. Drag
    // editing must mutate ONE persistent reference, so we clone the value once
    // here and treat `this._corners` as the single source of truth for span
    // rendering + dragging. Only connect/adopt/commit re-seed it from the value.
    this._corners = this._cloneCorners(this.cornersGeometryValue)

    // Precompute parallel arrays from [[s, k], …]:
    //   s[i]  — distance (m)
    //   kd[i] — DISPLAY curvature = -k (reconciles the app↔prototype sign flip)
    // maxK is over |raw k| so the amplitude normalisation matches the data.
    const raw = Array.isArray(this.curvatureValue) ? this.curvatureValue : []
    const s = new Array(raw.length)
    const kd = new Array(raw.length)
    let maxK = 0
    for (let i = 0; i < raw.length; i++) {
      const p = raw[i] || []
      const dist = Number(p[0]) || 0
      const k = Number(p[1]) || 0
      s[i] = dist
      kd[i] = -k
      const a = Math.abs(k)
      if (a > maxK) maxK = a
    }
    this._s = s
    this._kd = kd
    this._N = raw.length
    this._maxK = maxK || 1
    this._hasCurvature = this._N > 1

    // ResizeObserver drives re-render on width changes.
    this._W = this.hasHostTarget ? this.hostTarget.clientWidth || 960 : 960
    this._ro = new ResizeObserver((ents) => {
      for (const e of ents) {
        const w = e.contentRect.width
        if (w && Math.abs(w - this._W) > 0.5) {
          this._W = w
          this._render()
        }
      }
    })
    if (this.hasHostTarget) this._ro.observe(this.hostTarget)

    this._render()
  }

  disconnect() {
    if (this._ro) {
      this._ro.disconnect()
      this._ro = null
    }
    this._teardownDrag()
    this._teardownSpanDrag()
    this._teardownScopeDrag()
    this._teardownBoundaryDrag()
  }

  // ── Public API (Phase G bridge) ────────────────────────────────────────────

  // Push a distance window without re-emitting (avoids a map↔timeline loop).
  // null or a whole-lap span resets to the full track.
  setView(view) {
    const L = this._L
    if (
      !view ||
      typeof view.a !== "number" ||
      typeof view.b !== "number" ||
      view.b - view.a >= L * 0.999
    ) {
      this._view = { a: 0, b: L }
    } else {
      this._view = this._clampView(view.a, view.b)
    }
    this._render()
  }

  // Set the selected corner from outside (list-row click, map select). Does NOT
  // re-emit cornerSelect — that event is reserved for user clicks on a span rail.
  setSelected(number) {
    this._selectedNumber = number == null ? null : Number(number)
    this._render()
  }

  // Set the hovered corner from outside (list-row hover, map hover).
  setHovered(number) {
    this._hoveredNumber = number == null ? null : Number(number)
    this._render()
  }

  // Corners ⇄ Sections mode, pushed from the corner-curation parent. The
  // curvature waveform + zoom/scrub are shared across both modes; only the
  // overlay differs. For Phase B: in "sections" mode hide the corner spans
  // overlay (the section bands land in Phase C); in "corners" mode show them.
  setMode(mode) {
    const next = mode === "sections" ? "sections" : "corners"
    if (this._mode === next) return
    this._mode = next
    this._render()
  }

  // Replace the section tiling from the corner-curation parent (Phase C). Clones
  // into a stable working copy so a caller can't alias `_sections`, drops a stale
  // selection that no longer exists, and re-renders. The parent calls this on
  // mode→sections AND on every section-card reconnect (boundary/split/merge/rename
  // commit) so the bands + strip reflect the server's renumbered state.
  setSections(sectionsArray) {
    this._sections = this._cloneArray(sectionsArray)
    if (
      this._selectedSectionId != null &&
      !this._sections.some(
        (s) => String(s.id) === String(this._selectedSectionId),
      )
    ) {
      this._selectedSectionId = null
    }
    if (
      this._hoveredSectionId != null &&
      !this._sections.some(
        (s) => String(s.id) === String(this._hoveredSectionId),
      )
    ) {
      this._hoveredSectionId = null
    }
    this._render()
  }

  // Set the selected section from outside (list-row click, map select). Does NOT
  // re-emit sectionSelect (reserved for user clicks on a band).
  setSelectedSection(id) {
    this._selectedSectionId = id == null ? null : String(id)
    if (this._mode === "sections" && this._sectionBandEls?.length) {
      this._applySectionEmphasis()
      this._renderSectionStrip()
    } else {
      this._render()
    }
  }

  // Glow the hovered section's band from outside (list-row / map-arc hover routed
  // through the parent). null clears it. Transient — must NOT touch selection, and
  // is distinct from the boundary-handle hover (`_activeBoundary`). Does NOT re-emit
  // sectionHover (reserved for user hover on a band).
  setHoveredSection(id) {
    const next = id == null ? null : String(id)
    if (this._hoveredSectionId === next) return
    this._hoveredSectionId = next
    if (this._mode === "sections" && this._sectionBandEls?.length)
      this._applySectionEmphasis()
    else this._render()
  }

  // Replace the corners geometry from outside (a fresh array, e.g. after a
  // mutation elsewhere) and re-render. Adopts server renumbering. Clones into the
  // working copy so a caller can't alias `_corners`, and keeps the value in sync.
  adopt(cornersArray) {
    if (!Array.isArray(cornersArray)) return
    this._corners = this._cloneCorners(cornersArray)
    this.cornersGeometryValue = cornersArray
    this._render()
  }

  // ── View math (ported xOf / mOf / clampView) ───────────────────────────────

  get _span() {
    return this._view.b - this._view.a
  }

  _xOf(m) {
    return ((m - this._view.a) / this._span) * this._W
  }
  _mOf(px) {
    return this._view.a + (px / this._W) * this._span
  }

  _clampView(a, b) {
    const L = this._L
    let span = b - a
    if (span < MIN_SPAN) span = MIN_SPAN
    if (span > L) span = L
    if (a < 0) {
      a = 0
      b = a + span
    }
    if (b > L) {
      b = L
      a = b - span
    }
    if (a < 0) a = 0
    return { a, b: a + span }
  }

  // ── Waveform geometry (ported baseY / amp / yOf) ───────────────────────────

  get _baseY() {
    return WAVE_H / 2
  }
  get _amp() {
    return WAVE_H / 2 - 14
  }
  // kd already has the sign flip baked in; treat it as the prototype's k.
  _yOf(kd) {
    return this._baseY - (kd / this._maxK) * this._amp
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  _render() {
    if (!this.hasHostTarget) return
    // Connect-order guard: the corner-curation parent lives on the .contents
    // ancestor and pushes setMode/setSections to this outlet around the same time
    // this controller connects. If either lands BEFORE connect() runs, `_view`
    // (and the precomputed waveform/section state) aren't initialised yet — a
    // render would throw on `_view.b` (`get _span`). Bail until connect() has set
    // `_view`; connect() calls _render() at its end, so nothing is lost. Mirrors
    // the map controller's pre-state `_arc` guard.
    if (!this._view) return
    const host = this.hostTarget
    host.innerHTML = ""

    host.appendChild(this._buildHeader())

    const body = document.createElement("div")
    body.className = "px-5 pt-3 pb-4 select-none flex-1 min-h-0 flex flex-col"

    const plot = document.createElement("div")
    plot.className = "relative"
    plot.setAttribute("data-cw-plot", "")
    plot.style.height = `${WAVE_H + 30}px`
    plot.style.cursor = "crosshair"
    plot.appendChild(this._buildSvg())
    plot.appendChild(this._buildAxisLabels())
    this._wireScrubAndZoom(plot)
    this._wireScopeDrag(plot)
    // Corner spans are the Corners-mode overlay; in Sections mode the section
    // band overlay (tiling bands + boundary handles + ghosted corners) replaces
    // them. The Fit marquee is corner-mode only.
    if (this._mode === "sections") {
      plot.appendChild(this._buildBands(plot))
    } else {
      plot.appendChild(this._buildSpans())
      if (this._fitScope) plot.appendChild(this._buildFitMarquee())
    }
    body.appendChild(plot)

    // Pan scrollbar — only when zoomed in (a strict sub-window of the lap).
    if (this._span < this._L * 0.985) body.appendChild(this._buildScrollbar())

    host.appendChild(body)

    // Docked geometry strip (sibling of the host — re-rendering the host above
    // never touches it, but every _render keeps its field values in sync).
    this._renderStrip()
  }

  // ── Header ───────────────────────────────────────────────────────────────

  _buildHeader() {
    const header = document.createElement("div")
    header.className =
      "flex items-center gap-3 px-5 h-[52px] border-b border-app-border shrink-0"

    const title = document.createElement("h2")
    title.className =
      "font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-app-text-muted"
    title.textContent = "Corner geometry"
    header.appendChild(title)

    // L/R legend chips.
    const legend = document.createElement("span")
    legend.className =
      "hidden md:flex items-center gap-3 ml-1 text-[10.5px] font-mono text-app-text-faint"
    legend.appendChild(this._legendChip(LEFT_HUE, "Left"))
    legend.appendChild(this._legendChip(RIGHT_HUE, "Right"))
    header.appendChild(legend)

    // Usage hint.
    const hint = document.createElement("span")
    hint.className =
      "ml-auto hidden xl:flex items-center gap-1.5 text-[11px] text-app-text-faint"
    const hintSvg = svgEl("svg", {
      width: "13",
      height: "13",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.7",
    })
    hintSvg.appendChild(
      svgEl("path", {
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        d: "M3 12h18M7 8l-4 4 4 4M17 8l4 4-4 4",
      }),
    )
    hint.appendChild(hintSvg)
    hint.appendChild(
      document.createTextNode("Scroll to zoom · drag the bar to pan"),
    )
    header.appendChild(hint)

    // Zoom % + Full lap reset.
    const zoomWrap = document.createElement("div")
    zoomWrap.className = "flex items-center gap-1.5 xl:ml-3 ml-auto"
    const pct = document.createElement("span")
    pct.className =
      "font-mono text-[11px] text-app-text-faint tabular-nums w-[42px] text-right"
    const zoomPct = this._L > 0 ? Math.round((this._L / this._span) * 100) : 100
    pct.textContent = `${zoomPct}%`
    zoomWrap.appendChild(pct)

    const reset = document.createElement("button")
    reset.type = "button"
    reset.className =
      "h-7 px-2 rounded-md ring-1 ring-app-border text-[11.5px] font-medium text-app-text-secondary hover:bg-app-surface-hover disabled:opacity-35"
    reset.innerHTML = "Full&nbsp;lap"
    reset.disabled = this._span >= this._L
    reset.addEventListener("click", () => {
      this._view = { a: 0, b: this._L }
      this._render()
      this._emitViewChange()
    })
    zoomWrap.appendChild(reset)
    header.appendChild(zoomWrap)

    return header
  }

  _legendChip(hue, label) {
    const wrap = document.createElement("span")
    wrap.className = "flex items-center gap-1.5"
    const sw = document.createElement("span")
    sw.className = "w-2.5 h-2.5 rounded-[3px]"
    sw.style.background = hue
    wrap.appendChild(sw)
    wrap.appendChild(document.createTextNode(label))
    return wrap
  }

  // ── SVG waveform ───────────────────────────────────────────────────────────

  _buildSvg() {
    const W = this._W
    const svg = svgEl("svg", {
      width: W,
      height: WAVE_H,
      viewBox: `0 0 ${W} ${WAVE_H}`,
      class: "block",
      style: "position:absolute;top:0;left:0",
    })

    // Vertical two-tone gradient fill (cool above baseline, warm below).
    const baseY = this._baseY
    const defs = svgEl("defs")
    const grad = svgEl("linearGradient", {
      id: "cwWave",
      x1: "0",
      y1: "0",
      x2: "0",
      y2: WAVE_H,
      gradientUnits: "userSpaceOnUse",
    })
    grad.appendChild(
      svgEl("stop", {
        offset: "0",
        "stop-color": LEFT_HUE,
        "stop-opacity": "0.42",
      }),
    )
    grad.appendChild(
      svgEl("stop", {
        offset: (baseY / WAVE_H).toFixed(3),
        "stop-color": LEFT_HUE,
        "stop-opacity": "0.05",
      }),
    )
    grad.appendChild(
      svgEl("stop", {
        offset: (baseY / WAVE_H).toFixed(3),
        "stop-color": RIGHT_HUE,
        "stop-opacity": "0.05",
      }),
    )
    grad.appendChild(
      svgEl("stop", {
        offset: "1",
        "stop-color": RIGHT_HUE,
        "stop-opacity": "0.42",
      }),
    )
    defs.appendChild(grad)
    svg.appendChild(defs)

    const view = this._view

    // Gridlines at distance ticks.
    if (this._hasCurvature) {
      for (const m of this._ticks()) {
        svg.appendChild(
          svgEl("line", {
            x1: this._xOf(m),
            x2: this._xOf(m),
            y1: "0",
            y2: WAVE_H,
            stroke: "var(--cw-grid)",
            "stroke-width": "1",
          }),
        )
      }
    }

    // Sector boundaries — dashed (always show index 0; others when in view).
    const sectors = Array.isArray(this.sectorsValue) ? this.sectorsValue : []
    sectors.forEach((m, i) => {
      if ((m > view.a && m < view.b) || i === 0) {
        svg.appendChild(
          svgEl("line", {
            x1: this._xOf(m),
            x2: this._xOf(m),
            y1: "0",
            y2: WAVE_H,
            stroke: "var(--cw-axis)",
            "stroke-width": "1",
            "stroke-dasharray": "2 4",
          }),
        )
      }
    })

    // Zero baseline.
    svg.appendChild(
      svgEl("line", {
        x1: "0",
        x2: W,
        y1: baseY,
        y2: baseY,
        stroke: "var(--cw-axis)",
        "stroke-width": "1.2",
      }),
    )

    // Waveform fill + crisp two-tone stroke.
    if (this._hasCurvature) {
      const fillD = this._wavePath()
      if (fillD)
        svg.appendChild(svgEl("path", { d: fillD, fill: "url(#cwWave)" }))
      for (const seg of this._waveStroke()) {
        svg.appendChild(
          svgEl("path", {
            d: "M " + seg.pts.join(" L "),
            fill: "none",
            stroke: seg.sign === "L" ? LEFT_HUE : RIGHT_HUE,
            "stroke-width": "1.8",
            "stroke-linejoin": "round",
            opacity: "0.95",
          }),
        )
      }
    }

    // Start / finish verticals at 0 and L.
    for (const m of [0, this._L]) {
      if (m >= view.a - 1 && m <= view.b + 1) {
        svg.appendChild(
          svgEl("line", {
            x1: this._xOf(m),
            x2: this._xOf(m),
            y1: "0",
            y2: WAVE_H,
            stroke: "var(--cw-axis)",
            "stroke-width": "1.5",
          }),
        )
      }
    }

    return svg
  }

  // Distance ticks across the visible window.
  _ticks() {
    const step = niceStep(this._span)
    const out = []
    for (
      let m = Math.ceil(this._view.a / step) * step;
      m <= this._view.b;
      m += step
    )
      out.push(m)
    return out
  }

  // Filled area path: baseline → waveform points (within view + margin) → baseline → Z.
  _wavePath() {
    const { _s: s, _kd: kd, _N: N, _view: view, _span: span } = this
    const pts = []
    for (let i = 0; i < N; i++) {
      if (s[i] < view.a - span * 0.1 || s[i] > view.b + span * 0.1) continue
      pts.push([this._xOf(s[i]), this._yOf(kd[i])])
    }
    if (!pts.length) return ""
    let d = `M ${this._xOf(Math.max(view.a, s[0]))} ${this._baseY} `
    d +=
      "L " + pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" L ")
    d += ` L ${pts[pts.length - 1][0].toFixed(1)} ${this._baseY} Z`
    return d
  }

  // Crisp two-tone edge: split into runs by sign of kd (>=0 → "L"/blue, else "R"/orange).
  _waveStroke() {
    const { _s: s, _kd: kd, _N: N, _view: view, _span: span } = this
    const segs = []
    let cur = null
    for (let i = 0; i < N; i++) {
      if (s[i] < view.a - span * 0.1 || s[i] > view.b + span * 0.1) {
        cur = null
        continue
      }
      const sign = kd[i] >= 0 ? "L" : "R"
      const pt = `${this._xOf(s[i]).toFixed(1)} ${this._yOf(kd[i]).toFixed(1)}`
      if (!cur || cur.sign !== sign) {
        cur = { sign, pts: [pt] }
        segs.push(cur)
      } else cur.pts.push(pt)
    }
    return segs
  }

  // ── Axis-label strip (below the waveform) ──────────────────────────────────

  _buildAxisLabels() {
    const wrap = document.createElement("div")
    wrap.className = "absolute left-0 right-0 top-0 h-full pointer-events-none"
    const view = this._view

    // Distance ticks (skip the 0/near-start one — S/F owns that edge).
    for (const m of this._ticks()) {
      if (m <= 1) continue
      const span = document.createElement("span")
      span.className =
        "absolute font-mono text-[9.5px] text-app-text-faint tabular-nums"
      span.style.left = `${this._xOf(m)}px`
      span.style.top = `${WAVE_H + 5}px`
      span.style.transform = "translateX(-50%)"
      span.textContent = String(Math.round(m))
      wrap.appendChild(span)
    }

    // Sector labels S1/S2/S3…
    const sectors = Array.isArray(this.sectorsValue) ? this.sectorsValue : []
    sectors.forEach((m, i) => {
      if (!((m > view.a && m < view.b) || i === 0)) return
      const span = document.createElement("span")
      span.className = "absolute font-mono text-[9px] font-bold tracking-wider"
      span.style.left = `${this._xOf(m) + 4}px`
      span.style.top = `${WAVE_H + 16}px`
      span.style.color = "var(--cw-tick)"
      span.textContent = `S${i + 1}`
      wrap.appendChild(span)
    })

    // S/F markers at 0 and L.
    for (const m of [0, this._L]) {
      if (!(m >= view.a - 1 && m <= view.b + 1)) continue
      const span = document.createElement("span")
      span.className =
        "absolute flex items-center gap-1 font-mono text-[9px] font-bold"
      span.style.left =
        m === this._L ? `${this._xOf(m) - 4}px` : `${this._xOf(m) + 4}px`
      span.style.top = `${WAVE_H + 5}px`
      span.style.color = "var(--cw-tick)"
      if (m === this._L) span.style.transform = "translateX(-100%)"
      const dot = document.createElement("span")
      dot.className = "inline-block w-1.5 h-1.5 rounded-full"
      dot.style.background = "var(--cw-tick)"
      span.appendChild(dot)
      span.appendChild(document.createTextNode("S/F"))
      wrap.appendChild(span)
    }

    return wrap
  }

  // ── Scrub + wheel zoom ─────────────────────────────────────────────────────

  _wireScrubAndZoom(plot) {
    plot.addEventListener("wheel", (e) => this._onWheel(e, plot), {
      passive: false,
    })
    plot.addEventListener("mousemove", (e) => {
      // Don't scrub mid-drag (handle/rail edit, marquee scope, or boundary) — the
      // pointer is committed to that interaction.
      if (this._drag || this._scopeDrag || this._boundaryDrag) return
      const r = plot.getBoundingClientRect()
      this.dispatch("scrub", {
        detail: { m: this._mOf(e.clientX - r.left) },
        bubbles: true,
      })
    })
    plot.addEventListener("mouseleave", () => {
      this.dispatch("scrub", { detail: { m: null }, bubbles: true })
    })
  }

  // Cursor-centred wheel zoom (min span 140 m, clamped to the lap).
  _onWheel(e, plot) {
    e.preventDefault()
    const L = this._L
    if (!L) return
    const rect = plot.getBoundingClientRect()
    const px = e.clientX - rect.left
    const mc = this._mOf(px)
    const span = this._span
    const factor = e.deltaY > 0 ? 1.18 : 1 / 1.18
    const ns = Math.max(MIN_SPAN, Math.min(L, span * factor))
    const a = mc - (mc - this._view.a) * (ns / span)
    this._view = this._clampView(a, a + ns)
    this._render()
    this._emitViewChange()
  }

  // ── Pan scrollbar ──────────────────────────────────────────────────────────

  _buildScrollbar() {
    const L = this._L
    const span = this._span
    const track = document.createElement("div")
    track.className =
      "relative h-2.5 mt-3 rounded-full bg-app-surface-active/70 ring-1 ring-app-border-subtle"

    const thumb = document.createElement("div")
    thumb.className =
      "absolute top-0 h-2.5 rounded-full bg-app-text-faint hover:bg-app-text-muted cursor-grab active:cursor-grabbing transition-colors"
    thumb.style.left = `${(this._view.a / L) * 100}%`
    thumb.style.width = `${Math.max(6, (span / L) * 100)}%`
    thumb.style.touchAction = "none"
    thumb.title = "Drag to pan"

    thumb.addEventListener("pointerdown", (e) => {
      e.preventDefault()
      e.stopPropagation()
      const tw = track.getBoundingClientRect().width
      const startX = e.clientX
      const a0 = this._view.a
      const move = (ev) => {
        const dm = ((ev.clientX - startX) / tw) * L
        const a = Math.max(0, Math.min(L - span, a0 + dm))
        this._view = { a, b: a + span }
        this._render()
        this._emitViewChange()
      }
      const up = () => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", up)
        this._dragCleanup = null
      }
      this._dragCleanup = up
      window.addEventListener("pointermove", move)
      window.addEventListener("pointerup", up)
    })

    track.appendChild(thumb)
    return track
  }

  _teardownDrag() {
    if (this._dragCleanup) {
      this._dragCleanup()
      this._dragCleanup = null
    }
  }

  // ── Corner spans + handles (Phase E) ───────────────────────────────────────
  //
  // Each curated corner is a translucent band (start_m→end_m) with a solid title
  // rail (drag to move the whole corner) and three handles (start / apex / end,
  // each ew-resize). Ported from the designer prototype `design/cw-spans.jsx`.
  // The overlay lives INSIDE the same `plot` element as the waveform SVG, so it
  // shares `_xOf` and re-renders in the same pass — spans track zoom/pan/resize
  // frame-perfectly. Drag uses window pointermove/pointerup (decoupled from the
  // DOM) so mid-drag re-renders are safe.
  //
  // Provenance colours: manual/selected = vivid-purple (var(--vp) → fall back to
  // --color-vivid-purple); auto = teal #14b8c4. Only corners with a DB `id` are
  // editable — id-less (auto-only) corners render the band + rail for a complete
  // read of the lap, but their handles are inert and the rail is non-draggable.

  _accent() {
    const root = this.hasHostTarget ? this.hostTarget : this.element
    const vp =
      getComputedStyle(root).getPropertyValue("--vp").trim() ||
      getComputedStyle(root).getPropertyValue("--color-vivid-purple").trim()
    return vp || "#7c5cff"
  }

  _buildSpans() {
    const layer = document.createElement("div")
    layer.className = "absolute left-0 right-0 top-0"
    layer.style.height = `${WAVE_H}px`
    layer.style.pointerEvents = "none" // children opt back in

    const corners = Array.isArray(this._corners) ? this._corners : []
    const accent = this._accent()
    const TEAL = "#14b8c4"
    const W = this._W
    const h = WAVE_H
    // While a Fit scope is active, dim every non-active corner span to ~0.35 so
    // the marquee reads as the foreground interaction (matches cw-timeline.jsx
    // `dim={!!fitScope}`).
    const dim = !!this._fitScope

    for (const c of corners) {
      const x0 = this._xOf(c.start_m),
        x1 = this._xOf(c.end_m),
        xa = this._xOf(c.apex_m)
      if (x1 < -40 || x0 > W + 40) continue // culled off-screen

      const manual = c.source === "manual"
      const ctxColor = manual ? accent : TEAL
      const editable = c.id != null
      const number = Number(c.number)
      const selected = this._selectedNumber === number
      const hovered = this._hoveredNumber === number
      const active = selected || hovered
      const dimOpacity = dim && !active ? 0.35 : 1

      const bandFill = selected
        ? `color-mix(in srgb, ${accent} 16%, transparent)`
        : active
          ? `color-mix(in srgb, ${accent} 9%, transparent)`
          : "color-mix(in srgb, var(--app-text) 5%, transparent)"
      const railColor = active ? accent : ctxColor
      const borderTint = manual
        ? `color-mix(in srgb, ${accent} 45%, transparent)`
        : `color-mix(in srgb, ${TEAL} 45%, transparent)`

      // Band fill (visual only — keeps the waveform free for pan/zoom/scrub).
      const band = document.createElement("div")
      band.style.cssText =
        `position:absolute;left:${x0}px;width:${Math.max(2, x1 - x0)}px;top:0;height:${h}px;` +
        `background:${bandFill};border-left:1px solid ${borderTint};border-right:1px solid ${borderTint};` +
        `border-top-left-radius:5px;border-top-right-radius:5px;pointer-events:none;opacity:${dimOpacity};` +
        `z-index:${selected ? 10 : active ? 8 : 4}`
      layer.appendChild(band)

      // Interactive top rail — click selects, drag moves the whole corner.
      const rail = document.createElement("div")
      rail.style.cssText =
        `position:absolute;left:${x0}px;width:${Math.max(14, x1 - x0)}px;top:0;height:20px;` +
        `background:${railColor};opacity:${(selected ? 1 : active ? 0.85 : 0.55) * dimOpacity};` +
        `border-top-left-radius:5px;border-top-right-radius:5px;touch-action:none;` +
        `cursor:${editable ? "grab" : "default"};pointer-events:auto;z-index:${selected ? 20 : 9}`
      rail.title = editable
        ? "Drag to move the whole corner"
        : "Auto corner — run Fit to edit"

      const labelWrap = document.createElement("span")
      labelWrap.className =
        "absolute inset-0 flex items-center gap-1 px-1.5 truncate"
      labelWrap.style.cssText +=
        "color:#fff;font-size:10.5px;font-weight:700;font-family:'JetBrains Mono',monospace;pointer-events:none"
      const numSpan = document.createElement("span")
      numSpan.textContent = c.number
      labelWrap.appendChild(numSpan)
      const named = c.name && String(c.name).trim()
      if (x1 - x0 > 60) {
        const nameSpan = document.createElement("span")
        nameSpan.className = "truncate"
        nameSpan.style.cssText =
          "font-family:'Space Grotesk',sans-serif;font-weight:600;opacity:0.95"
        nameSpan.textContent = named ? c.name : `Turn ${c.number}`
        labelWrap.appendChild(nameSpan)
      }
      if (x1 - x0 > 38) {
        const dirSpan = document.createElement("span")
        dirSpan.style.opacity = "0.8"
        dirSpan.textContent = c.direction === "left" ? "L" : "R"
        labelWrap.appendChild(dirSpan)
      }
      rail.appendChild(labelWrap)

      rail.addEventListener("mouseenter", () => this._onCornerHover(number))
      rail.addEventListener("mouseleave", () => this._onCornerHover(null))
      rail.addEventListener("pointerdown", (e) => {
        e.stopPropagation()
        this._onCornerSelect(number)
        if (editable) this._startSpanDrag(e, c, "rail")
      })
      layer.appendChild(rail)

      // Selected apex readout chip — pinned just above the apex.
      if (selected) {
        const chip = document.createElement("div")
        chip.className =
          "px-1.5 py-0.5 rounded bg-app-popover-bg ring-1 ring-app-popover-ring shadow"
        chip.style.cssText += `position:absolute;left:${Math.max(2, Math.min(W - 64, xa - 30))}px;top:22px;z-index:30;pointer-events:none`
        const m = document.createElement("span")
        m.className = "font-mono text-[10px] font-bold tabular-nums"
        m.style.color = accent
        m.innerHTML = `${Math.round(c.apex_m)}<span class="text-app-text-faint font-normal"> m</span>`
        chip.appendChild(m)
        layer.appendChild(chip)
      }

      // Three handles (start / apex / end). Visible only when active.
      layer.appendChild(
        this._buildHandle(c, x0, "start", {
          accent,
          ctxColor,
          selected,
          active,
          editable,
          h,
        }),
      )
      layer.appendChild(
        this._buildHandle(c, xa, "apex", {
          accent,
          ctxColor,
          selected,
          active,
          editable,
          h,
        }),
      )
      layer.appendChild(
        this._buildHandle(c, x1, "end", {
          accent,
          ctxColor,
          selected,
          active,
          editable,
          h,
        }),
      )
    }

    return layer
  }

  _buildHandle(
    c,
    x,
    which,
    { accent, ctxColor, selected, active, editable, h },
  ) {
    const isApex = which === "apex"
    const col = selected ? accent : ctxColor
    const wrap = document.createElement("div")
    wrap.style.cssText =
      `position:absolute;left:${x}px;top:0;height:${h}px;transform:translateX(-50%);` +
      `width:${isApex ? 14 : 12}px;cursor:${editable ? "ew-resize" : "default"};` +
      `z-index:${selected ? 24 : 12};touch-action:none;opacity:${active ? 1 : 0};` +
      `pointer-events:${active && editable ? "auto" : "none"}`

    // Vertical line.
    const line = document.createElement("div")
    if (isApex) {
      line.style.cssText =
        `position:absolute;left:50%;top:0;height:${h}px;width:2px;` +
        `transform:translateX(-50%);background:${col};opacity:0.95;border-radius:2px`
    } else {
      line.style.cssText =
        `position:absolute;left:50%;top:20px;height:${h - 20}px;width:1.5px;` +
        `transform:translateX(-50%);background:transparent;border-left:1px dashed ${col};opacity:0.7`
    }
    wrap.appendChild(line)

    // Grab tab.
    const tab = document.createElement("div")
    if (isApex) {
      tab.style.cssText =
        `position:absolute;left:50%;top:-3px;transform:translateX(-50%) rotate(45deg);` +
        `width:9px;height:9px;background:${col};border-radius:2px;box-shadow:0 1px 3px rgba(0,0,0,.4)`
    } else {
      tab.style.cssText =
        `position:absolute;left:50%;top:${h / 2 - 10}px;transform:translateX(-50%);` +
        `width:7px;height:20px;background:${col};border-radius:3px;box-shadow:0 1px 3px rgba(0,0,0,.35)`
    }
    wrap.appendChild(tab)

    if (editable) {
      wrap.addEventListener("mouseenter", () =>
        this._onCornerHover(Number(c.number)),
      )
      wrap.addEventListener("pointerdown", (e) => {
        e.stopPropagation()
        this._onCornerSelect(Number(c.number))
        this._startSpanDrag(e, c, which)
      })
    }
    return wrap
  }

  // ── Span/handle drag ───────────────────────────────────────────────────────

  // Begin a handle/rail drag. `kind` is "start" | "apex" | "end" | "rail". We
  // snapshot the corner's window at grab time and re-derive geometry from the
  // pointer position on each move; window pointer listeners survive re-renders.
  _startSpanDrag(e, corner, kind) {
    e.preventDefault()
    if (!this.hasHostTarget) return
    const plot = this.hostTarget.querySelector("[data-cw-plot]")
    const rect = (plot || this.hostTarget).getBoundingClientRect()

    this._drag = {
      id: corner.id,
      number: Number(corner.number),
      kind,
      rect,
      view: { a: this._view.a, b: this._view.b },
      // Snapshot at grab.
      s0: corner.start_m,
      a0: corner.apex_m,
      e0: corner.end_m,
      startM: this._mOfAt(e.clientX - rect.left, {
        a: this._view.a,
        b: this._view.b,
      }),
      moved: false,
    }

    const move = (ev) => this._onSpanMove(ev)
    const up = () => this._onSpanUp()
    this._spanMove = move
    this._spanUp = up
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  // Map a pixel offset to meters using a SPECIFIC view (the grab-time view, so a
  // mid-drag re-render that recomputes _view doesn't skew the math).
  _mOfAt(px, view) {
    return view.a + (px / this._W) * (view.b - view.a)
  }

  _onSpanMove(ev) {
    const d = this._drag
    if (!d) return
    const L = this._L
    const m = this._mOfAt(ev.clientX - d.rect.left, d.view)
    d.moved = true

    const corner = this._cornerByNumber(d.number)
    if (!corner) return

    if (d.kind === "rail") {
      // Move the whole corner: shift start/apex/end together, clamped so the
      // whole span stays in [0, length].
      const dm = m - d.startM
      const sh = Math.max(-d.s0, Math.min(L - d.e0, dm))
      corner.start_m = Math.round(d.s0 + sh)
      corner.apex_m = Math.round(d.a0 + sh)
      corner.end_m = Math.round(d.e0 + sh)
    } else {
      const mm = Math.max(0, Math.min(L, m))
      if (d.kind === "start") {
        // start clamps to ≤ apex.
        corner.start_m = Math.round(Math.min(mm, corner.apex_m))
      } else if (d.kind === "end") {
        // end clamps to ≥ apex.
        corner.end_m = Math.round(Math.max(mm, corner.apex_m))
      } else {
        // apex clamps within [start, end].
        corner.apex_m = Math.round(
          Math.max(corner.start_m, Math.min(corner.end_m, mm)),
        )
      }
    }
    corner.source = "manual"

    this._render()
    this.dispatch("cornerEdit", {
      detail: {
        id: d.id,
        number: d.number,
        start_m: corner.start_m,
        apex_m: corner.apex_m,
        end_m: corner.end_m,
        // Full working-corner array so the map can update its arc/marker live
        // during the drag (Phase G live-link). Shallow clone so the consumer
        // can't alias our mutable source of truth.
        corners: this._corners.map((c) => ({ ...c })),
        committed: false,
      },
      bubbles: true,
    })
  }

  _onSpanUp() {
    const d = this._drag
    this._teardownSpanDrag()
    if (!d) return
    const corner = this._cornerByNumber(d.number)
    // No id (defensive — drags are gated to editable corners) or no real move:
    // re-render from current client state and stop.
    if (!corner || d.id == null) {
      this._render()
      return
    }
    this._persistGeometry(d.id, {
      start_m: corner.start_m,
      apex_m: corner.apex_m,
      end_m: corner.end_m,
    })
  }

  _teardownSpanDrag() {
    if (this._spanMove) {
      window.removeEventListener("pointermove", this._spanMove)
      this._spanMove = null
    }
    if (this._spanUp) {
      window.removeEventListener("pointerup", this._spanUp)
      this._spanUp = null
    }
    this._drag = null
  }

  _cornerByNumber(number) {
    const corners = Array.isArray(this._corners) ? this._corners : []
    return corners.find((c) => Number(c.number) === Number(number))
  }

  // ── Fit marquee (Phase F1) ─────────────────────────────────────────────────
  //
  // Dragging across EMPTY track scopes a Fit range. The plot background's
  // pointerdown is free (scrub is on mousemove; spans/handles stopPropagation),
  // so it's where the scope drag lives. Three drag kinds share one window
  // pointermove/up pair, decoupled from the DOM so mid-drag re-renders are safe:
  //   "scope"     — fresh selection: grab-M → current-M
  //   "movescope" — drag the whole marquee body (cursor: move)
  //   "edge"      — resize one edge ("start"/"end"); the opposite edge is the anchor
  // On release, a "scope" drag that moved < 25 m is a click on empty track →
  // clear the scope and emit a deselect (cornerSelect { number: null }).

  _wireScopeDrag(plot) {
    plot.addEventListener("pointerdown", (e) => this._onScopeDown(e, plot))
  }

  _onScopeDown(e, plot) {
    if (e.button != null && e.button !== 0) return
    // Sections mode: the Fit marquee is corner-only. The bands cover the plot and
    // own selection (stopPropagation); reaching here means a click slipped through
    // to empty plot → deselect the section.
    if (this._mode === "sections") {
      if (this._selectedSectionId != null) this._onSectionSelect(null)
      return
    }
    // Spans, handles, and the marquee chrome stopPropagation, so reaching here
    // means the pointer landed on empty track → start a fresh scope.
    this._startScopeDrag(e, plot, "scope")
  }

  // Begin a scope/marquee drag. `kind` ∈ "scope" | "movescope" | "edge".
  // For "edge", `edge` is "start"|"end"; the anchor is the opposite edge's metre.
  _startScopeDrag(e, plot, kind, edge = null) {
    e.preventDefault()
    if (!this.hasHostTarget) return
    const host = plot || this.hostTarget.querySelector("[data-cw-plot]")
    const rect = (host || this.hostTarget).getBoundingClientRect()
    const view = { a: this._view.a, b: this._view.b }
    const grabM = this._mOfAt(e.clientX - rect.left, view)

    const d = { kind, rect, view, grabM, edge, moved: false, lastM: grabM }
    if (kind === "movescope" && this._fitScope) {
      d.a0 = this._fitScope.start_m
      d.w = this._fitScope.end_m - this._fitScope.start_m
    } else if (kind === "edge" && this._fitScope) {
      // Anchor = the edge we're NOT dragging; the moving edge tracks the pointer.
      d.anchorM =
        edge === "start" ? this._fitScope.end_m : this._fitScope.start_m
    }
    this._scopeDrag = d

    const move = (ev) => this._onScopeMove(ev)
    const up = () => this._onScopeUp()
    this._scopeMove = move
    this._scopeUp = up
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  _onScopeMove(ev) {
    const d = this._scopeDrag
    if (!d) return
    const L = this._L
    const m = this._mOfAt(ev.clientX - d.rect.left, d.view)
    d.moved = true
    d.lastM = m

    if (d.kind === "movescope") {
      // Shift the whole marquee, clamped so the band stays within [0, L].
      const dm = m - d.grabM
      let a = Math.max(0, Math.min(L - d.w, d.a0 + dm))
      this._fitScope = { start_m: a, end_m: a + d.w }
    } else {
      // "scope" (fresh) and "edge" (resize) both span between an anchor and the
      // pointer; normalize so start ≤ end. For "scope" the anchor is the grab-M;
      // for "edge" it's the fixed opposite edge.
      const anchor = d.kind === "edge" ? d.anchorM : d.grabM
      const mm = Math.max(0, Math.min(L, m))
      this._fitScope = {
        start_m: Math.max(0, Math.min(anchor, mm)),
        end_m: Math.min(L, Math.max(anchor, mm)),
      }
    }
    this._render()
  }

  _onScopeUp() {
    const d = this._scopeDrag
    this._teardownScopeDrag()
    if (!d) return
    // A fresh scope drag that barely moved is really a click on empty track →
    // clear the scope AND deselect (matches cw-timeline.jsx onUp).
    if (d.kind === "scope") {
      const moved = Math.abs((d.lastM ?? d.grabM) - d.grabM)
      if (moved < 25) {
        this._fitScope = null
        this._selectedNumber = null
        this._render()
        this.dispatch("cornerSelect", {
          detail: { number: null },
          bubbles: true,
        })
        return
      }
    }
    this._render()
  }

  _teardownScopeDrag() {
    if (this._scopeMove) {
      window.removeEventListener("pointermove", this._scopeMove)
      this._scopeMove = null
    }
    if (this._scopeUp) {
      window.removeEventListener("pointerup", this._scopeUp)
      this._scopeUp = null
    }
    this._scopeDrag = null
  }

  // Render the dashed/hatched marquee band + two resize-edge grips + a draggable
  // body, plus the floating toolbar pinned above it. Ported from FitMarquee in
  // cw-spans.jsx. Shares `_xOf`, so it tracks zoom/pan/resize frame-perfectly.
  _buildFitMarquee() {
    const scope = this._fitScope
    const accent = this._accent()
    const W = this._W
    const h = WAVE_H
    const x0 = this._xOf(scope.start_m)
    const x1 = this._xOf(scope.end_m)
    const w = Math.max(2, x1 - x0)
    const len = Math.round(scope.end_m - scope.start_m)
    const tooNarrow = len < 30

    const layer = document.createElement("div")
    layer.className = "absolute left-0 right-0 top-0"
    layer.style.height = `${h}px`
    layer.style.pointerEvents = "none" // children opt back in

    // Hatched, dashed scope band — draggable body (movescope).
    const band = document.createElement("div")
    band.style.cssText =
      `position:absolute;left:${x0}px;width:${w}px;top:0;height:${h}px;z-index:28;` +
      `border:1.5px dashed ${accent};border-radius:6px;cursor:move;touch-action:none;pointer-events:auto;` +
      `background:repeating-linear-gradient(45deg, color-mix(in srgb, ${accent} 14%, transparent) 0 7px, transparent 7px 14px)`
    band.addEventListener("pointerdown", (e) => {
      e.stopPropagation()
      this._startScopeDrag(e, layer.parentElement, "movescope")
    })
    layer.appendChild(band)

    // Two resize-edge grips.
    for (const edge of ["start", "end"]) {
      const ex = (edge === "start" ? x0 : x1) - 5
      const grip = document.createElement("div")
      grip.style.cssText =
        `position:absolute;left:${ex}px;width:10px;top:0;height:${h}px;` +
        `cursor:ew-resize;z-index:29;touch-action:none;pointer-events:auto`
      const knob = document.createElement("div")
      knob.style.cssText =
        `position:absolute;left:50%;top:${h / 2 - 11}px;transform:translateX(-50%);` +
        `width:6px;height:22px;border-radius:3px;background:${accent}`
      grip.appendChild(knob)
      grip.addEventListener("pointerdown", (e) => {
        e.stopPropagation()
        this._startScopeDrag(e, layer.parentElement, "edge", edge)
      })
      layer.appendChild(grip)
    }

    // Floating toolbar pinned above the marquee, clamped within the plot width.
    layer.appendChild(
      this._buildFitToolbar({ x0, w, W, len, tooNarrow, accent }),
    )

    return layer
  }

  _buildFitToolbar({ x0, w, W, len, tooNarrow, accent }) {
    const count = this._fitCount
    const barW = count === 2 ? 392 : 380
    const barLeft = Math.max(
      2,
      Math.min((W || 960) - barW - 2, x0 + w / 2 - barW / 2),
    )

    const bar = document.createElement("div")
    bar.className =
      "flex items-center gap-2 px-2 py-1.5 rounded-xl bg-app-popover-bg ring-1 ring-app-popover-ring shadow-xl"
    bar.style.cssText = `position:absolute;left:${barLeft}px;top:-52px;z-index:40;pointer-events:auto`
    // The toolbar's own pointer events must NOT start a new scope drag.
    bar.addEventListener("pointerdown", (e) => e.stopPropagation())

    // Sparkle glyph.
    const spark = svgEl("svg", {
      width: "14",
      height: "14",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: accent,
      "stroke-width": "1.7",
      class: "shrink-0",
    })
    spark.appendChild(
      svgEl("path", {
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        d: "M5 3v4M3 5h4M6 17v4M4 19h4M13 3l2.5 6.5L22 12l-6.5 2.5L13 21l-2.5-6.5L4 12l6.5-2.5z",
      }),
    )
    bar.appendChild(spark)

    // Range length (mono).
    const lenSpan = document.createElement("span")
    lenSpan.className =
      "font-mono text-[10.5px] text-app-text-muted tabular-nums whitespace-nowrap"
    lenSpan.textContent = `${len.toLocaleString()} m`
    bar.appendChild(lenSpan)

    const dot = document.createElement("span")
    dot.className = "text-app-border-strong"
    dot.textContent = "·"
    bar.appendChild(dot)

    // Segmented 1 corner / 2 corners.
    const seg = document.createElement("div")
    seg.className =
      "inline-flex p-0.5 rounded-md bg-app-surface-alt ring-1 ring-app-border-subtle"
    for (const n of [1, 2]) {
      const btn = document.createElement("button")
      btn.type = "button"
      const onState = count === n
      btn.className =
        "px-2 h-6 rounded text-[11.5px] font-semibold transition-colors " +
        (onState
          ? "bg-vivid-purple text-white"
          : "text-app-text-secondary hover:bg-app-surface-hover")
      btn.textContent = `${n} corner${n > 1 ? "s" : ""}`
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        this._setFitCount(n)
      })
      seg.appendChild(btn)
    }
    bar.appendChild(seg)

    // Fit (ghost) + Magic (solid).
    const fitBtn = document.createElement("button")
    fitBtn.type = "button"
    fitBtn.className = "apx-btn apx-btn--ghost apx-btn--sm"
    fitBtn.textContent = "Fit"
    fitBtn.disabled = tooNarrow
    if (tooNarrow) fitBtn.style.opacity = "0.4"
    fitBtn.addEventListener("click", (e) => {
      e.stopPropagation()
      if (!tooNarrow) this._runFit(false)
    })
    bar.appendChild(fitBtn)

    const magicBtn = document.createElement("button")
    magicBtn.type = "button"
    magicBtn.className = "apx-btn apx-btn--solid apx-btn--sm"
    magicBtn.title = "Fit, then optimise the racing line"
    magicBtn.disabled = tooNarrow
    if (tooNarrow) magicBtn.style.opacity = "0.4"
    const mSvg = svgEl("svg", {
      width: "13",
      height: "13",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.8",
    })
    mSvg.appendChild(
      svgEl("path", {
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        d: "M5 3v4M3 5h4M6 17v4M4 19h4M13 3l2.5 6.5L22 12l-6.5 2.5L13 21l-2.5-6.5L4 12l6.5-2.5z",
      }),
    )
    magicBtn.appendChild(mSvg)
    magicBtn.appendChild(document.createTextNode("Magic"))
    magicBtn.addEventListener("click", (e) => {
      e.stopPropagation()
      if (!tooNarrow) this._runFit(true)
    })
    bar.appendChild(magicBtn)

    // × cancel.
    const cancel = document.createElement("button")
    cancel.type = "button"
    cancel.className =
      "w-6 h-6 grid place-items-center rounded-md text-app-text-faint hover:bg-app-surface-hover hover:text-app-text"
    cancel.setAttribute("aria-label", "Cancel")
    const xSvg = svgEl("svg", {
      width: "13",
      height: "13",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2",
    })
    xSvg.appendChild(
      svgEl("path", { "stroke-linecap": "round", d: "M6 6l12 12M18 6L6 18" }),
    )
    cancel.appendChild(xSvg)
    cancel.addEventListener("click", (e) => {
      e.stopPropagation()
      this._cancelFit()
    })
    bar.appendChild(cancel)

    return bar
  }

  _setFitCount(n) {
    this._fitCount = n === 1 ? 1 : 2
    this._render()
  }

  _cancelFit() {
    this._fitScope = null
    this._render()
  }

  // POST the scoped range to the Fit endpoint. `magic` chooses Fit vs Magic.
  // The server returns the card_stream Turbo Stream (corners-card + hero-map
  // replace); we render it, clear the scope, and let the corner-curation
  // parent's reconnected map outlet re-sync THIS timeline off the server's
  // freshly-rendered, split + renumbered geometry.
  _runFit(magic) {
    const scope = this._fitScope
    if (!scope) return
    const url = this.fitUrlValue
    if (!url) {
      this._cancelFit()
      return
    }
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    const start_m = Math.round(scope.start_m)
    const end_m = Math.round(scope.end_m)

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ start_m, end_m, count: this._fitCount, magic }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`fit POST ${res.status}`)
        return res.text()
      })
      .then((html) => {
        this._fitScope = null
        window.Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        // Leave the scope in place so the user can retry; just re-render.
        this._render()
      })
  }

  // ── Geometry strip (Phase F2) ──────────────────────────────────────────────
  //
  // The numeric editing surface docked under the timeline. Ported from the
  // designer prototype's GeometryStrip / NumField / DirToggleD (cw-detail.jsx).
  //
  // Render model — the CRITICAL part. `_render()` fires rapidly during a handle
  // drag; rebuilding the strip's <input> nodes each time would kill a focused
  // input mid-type and fight the live drag. So:
  //   • The strip DOM is built ONCE per selection identity (corner number + id),
  //     keyed by `_stripKey`.
  //   • Every subsequent `_render()` only UPDATES values in place — sets each
  //     input.value (skipping a focused input so typing isn't clobbered), the
  //     Length readout, and the direction active state.
  //   • A full rebuild happens only when `_stripKey` changes (selection change
  //     or the selected corner's identity changes), or selection clears.
  // This is what makes handle-drag → field updates smooth AND lets the user type
  // a value without it being overwritten by an intervening re-render.
  _renderStrip() {
    if (!this.hasStripTarget) return
    // In Sections mode the docked strip becomes the SectionStrip, not the corner
    // geometry strip.
    if (this._mode === "sections") {
      this._renderSectionStrip()
      return
    }
    const strip = this.stripTarget
    // Switching back from the section strip → force a corner-strip rebuild.
    this._secStripKey = undefined

    const corner =
      this._selectedNumber == null
        ? null
        : this._cornerByNumber(this._selectedNumber)

    // Identity key the current DOM was built for. id is included so a renumber
    // that swaps which row sits at this number forces a rebuild.
    const key = corner
      ? `${corner.number}:${corner.id == null ? "" : corner.id}`
      : "none"

    if (key !== this._stripKey) {
      // Selection identity changed → rebuild the strip DOM from scratch.
      this._stripKey = key
      this._stripFields = null
      this._stripLength = null
      this._stripDirBtns = null
      strip.innerHTML = ""
      strip.className = corner
        ? "shrink-0 border-t border-app-border bg-app-surface-alt"
        : "shrink-0 border-t border-app-border"
      strip.appendChild(
        corner ? this._buildStripSelected(corner) : this._buildStripHint(),
      )
      return // freshly built with current values; nothing to update in place
    }

    // Same selection identity → update values in place (no DOM teardown).
    if (corner) this._updateStripValues(corner)
  }

  // Null-selection hint row (GeometryStrip's null branch).
  _buildStripHint() {
    const row = document.createElement("div")
    row.className =
      "flex items-center gap-2.5 px-5 py-3 text-[12.5px] text-app-text-muted"

    const svg = svgEl("svg", {
      width: "15",
      height: "15",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.7",
      class: "text-app-text-faint shrink-0",
    })
    svg.appendChild(svgEl("path", { d: "M12 3a9 9 0 1 0 9 9" }))
    svg.appendChild(
      svgEl("circle", {
        cx: "12",
        cy: "12",
        r: "1.6",
        fill: "currentColor",
        stroke: "none",
      }),
    )
    row.appendChild(svg)

    const span = document.createElement("span")
    const b = document.createElement("b")
    b.className = "text-app-text-secondary font-semibold"
    b.textContent = "Select a corner"
    span.appendChild(b)
    span.appendChild(
      document.createTextNode(
        " on the timeline to fine-tune its geometry — or ",
      ),
    )
    const drag = document.createElement("span")
    drag.className = "font-semibold"
    drag.style.color = this._accent()
    drag.textContent = "drag across empty track"
    span.appendChild(drag)
    span.appendChild(document.createTextNode(" to scope and place a new one."))
    row.appendChild(span)

    return row
  }

  // Selected-corner editing surface: chip · Direction · Start · Apex · End ·
  // Length · Magic fit · Remove. Stores live nodes on `this._strip*` so
  // `_updateStripValues` can mutate them in place.
  _buildStripSelected(corner) {
    const accent = this._accent()
    const TEAL = "#14b8c4"
    const editable = corner.id != null

    const row = document.createElement("div")
    row.className = "flex items-end gap-x-4 gap-y-3 flex-wrap px-5 py-3.5"

    // ── Selected chip (number badge + name / Turn N italic).
    const selWrap = document.createElement("div")
    selWrap.className = "flex flex-col gap-1 mr-1"
    selWrap.appendChild(this._stripLabel("Selected"))
    const chipRow = document.createElement("div")
    chipRow.className = "flex items-center gap-2 h-9"
    const badge = document.createElement("span")
    badge.className =
      "w-7 h-7 grid place-items-center rounded-lg font-mono text-[12px] font-bold"
    badge.style.background = `color-mix(in srgb, ${accent} 15%, transparent)`
    badge.style.color = accent
    badge.textContent = corner.number
    chipRow.appendChild(badge)
    const named = corner.name && String(corner.name).trim()
    const nameSpan = document.createElement("span")
    nameSpan.className = named
      ? "text-[14px] font-semibold text-app-text"
      : "text-[14px] italic text-app-text-faint"
    nameSpan.textContent = named ? corner.name : `Turn ${corner.number}`
    chipRow.appendChild(nameSpan)
    selWrap.appendChild(chipRow)
    row.appendChild(selWrap)

    // ── Direction toggle (L cool / R warm).
    const dirWrap = document.createElement("div")
    dirWrap.className = "flex flex-col gap-1"
    dirWrap.appendChild(this._stripLabel("Direction"))
    dirWrap.appendChild(this._buildDirToggle(corner, editable))
    row.appendChild(dirWrap)

    // ── Divider.
    const divider = document.createElement("div")
    divider.className = "w-px self-stretch bg-app-border my-1 hidden sm:block"
    row.appendChild(divider)

    // ── Start / Apex / End numeric fields.
    this._stripFields = {}
    row.appendChild(
      this._buildNumField(corner, "start", "Start", TEAL, editable),
    )
    row.appendChild(
      this._buildNumField(corner, "apex", "Apex", accent, editable),
    )
    row.appendChild(this._buildNumField(corner, "end", "End", TEAL, editable))

    // ── Length readout (end − start).
    const lenWrap = document.createElement("div")
    lenWrap.className = "flex flex-col gap-1"
    lenWrap.appendChild(this._stripLabel("Length"))
    const lenVal = document.createElement("div")
    lenVal.className =
      "h-9 flex items-center font-mono text-[13px] tabular-nums text-app-text-secondary"
    lenVal.textContent = `${Math.round(corner.end_m - corner.start_m)} m`
    this._stripLength = lenVal
    lenWrap.appendChild(lenVal)
    row.appendChild(lenWrap)

    // ── Magic fit (ghost) — recompute entry/apex/exit on a late-apex line.
    const magicBtn = document.createElement("button")
    magicBtn.type = "button"
    magicBtn.className = "apx-btn apx-btn--ghost apx-btn--sm ml-auto mb-0.5"
    magicBtn.title = "Recompute entry / apex / exit on an optimal racing line"
    const mSvg = svgEl("svg", {
      width: "14",
      height: "14",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.8",
    })
    mSvg.appendChild(
      svgEl("path", {
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        d: "M5 3v4M3 5h4M6 17v4M4 19h4M13 3l2.5 6.5L22 12l-6.5 2.5L13 21l-2.5-6.5L4 12l6.5-2.5z",
      }),
    )
    magicBtn.appendChild(mSvg)
    magicBtn.appendChild(document.createTextNode("Magic fit"))
    magicBtn.disabled = !editable
    if (!editable) magicBtn.style.opacity = "0.4"
    magicBtn.addEventListener("click", () => {
      if (editable) this._magicFitCorner(corner.id)
    })
    row.appendChild(magicBtn)

    // ── Remove (danger) — opens the styled confirm dialog.
    const rmBtn = document.createElement("button")
    rmBtn.type = "button"
    rmBtn.className = "apx-btn apx-btn--danger apx-btn--sm mb-0.5"
    const rSvg = svgEl("svg", {
      width: "14",
      height: "14",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.7",
    })
    rSvg.appendChild(
      svgEl("path", {
        "stroke-linecap": "round",
        d: "M4 7h16M9 7V5h6v2M7 7l1 12h8l1-12",
      }),
    )
    rmBtn.appendChild(rSvg)
    rmBtn.appendChild(document.createTextNode("Remove"))
    rmBtn.disabled = !editable
    if (!editable) rmBtn.style.opacity = "0.4"
    rmBtn.addEventListener("click", () => {
      if (editable) this._openRemoveDialog(corner)
    })
    row.appendChild(rmBtn)

    return row
  }

  _stripLabel(text) {
    const s = document.createElement("span")
    s.className =
      "text-[9.5px] uppercase tracking-[0.12em] font-semibold text-app-text-faint"
    s.textContent = text
    return s
  }

  // L/R segmented toggle. Persists the chosen direction via _persistAttrs.
  _buildDirToggle(corner, editable) {
    const wrap = document.createElement("div")
    wrap.className =
      "inline-flex p-0.5 rounded-lg bg-app-input-bg ring-1 ring-app-input-ring h-9"
    this._stripDirBtns = {}
    for (const [v, label, hue] of [
      ["left", "L", LEFT_HUE],
      ["right", "R", RIGHT_HUE],
    ]) {
      const btn = document.createElement("button")
      btn.type = "button"
      const on = corner.direction === v
      btn.className =
        "w-9 rounded-md text-[12px] font-mono font-bold transition-colors " +
        (on
          ? "text-white"
          : "text-app-text-faint hover:text-app-text-secondary")
      if (on) btn.style.background = hue
      btn.dataset.hue = hue
      btn.textContent = label
      if (editable) {
        btn.addEventListener("click", () => this._setDirection(corner, v))
      } else {
        btn.disabled = true
      }
      this._stripDirBtns[v] = btn
      wrap.appendChild(btn)
    }
    return wrap
  }

  // Numeric field with steppers + clamp-on-commit. Mirrors NumField. The clamp
  // bounds are read LIVE from the working-copy corner at commit time (so the
  // Start↔Apex↔End interdependencies use current values, not grab-time ones).
  _buildNumField(corner, which, label, color, editable) {
    const wrap = document.createElement("div")
    wrap.className = "flex flex-col gap-1"

    const lab = document.createElement("span")
    lab.className =
      "text-[9.5px] uppercase tracking-[0.12em] font-semibold flex items-center gap-1.5"
    lab.style.color = color
    const dot = document.createElement("span")
    dot.className = "w-1.5 h-1.5 rounded-full"
    dot.style.background = color
    lab.appendChild(dot)
    lab.appendChild(document.createTextNode(label))
    wrap.appendChild(lab)

    const box = document.createElement("div")
    box.className =
      "inline-flex items-center h-9 rounded-lg bg-app-input-bg ring-1 ring-app-input-ring focus-within:ring-2 focus-within:ring-vivid-purple overflow-hidden"

    const input = document.createElement("input")
    input.type = "text"
    input.inputMode = "numeric"
    input.className =
      "w-[58px] h-full bg-transparent px-2.5 text-[13px] font-mono tabular-nums text-app-text outline-none text-right"
    input.value = String(Math.round(corner[`${which}_m`]))
    input.disabled = !editable
    input.addEventListener("input", () => {
      input.value = input.value.replace(/[^0-9]/g, "")
    })
    input.addEventListener("blur", () => this._commitField(which, input.value))
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") input.blur()
    })
    box.appendChild(input)

    const unit = document.createElement("span")
    unit.className = "text-[10px] font-mono text-app-text-faint pr-1.5"
    unit.textContent = "m"
    box.appendChild(unit)

    const steppers = document.createElement("div")
    steppers.className =
      "flex flex-col border-l border-app-border-subtle h-full"
    const up = this._stepperBtn("M18 15l-6-6-6 6", false)
    const dn = this._stepperBtn("M6 9l6 6 6-6", true)
    if (editable) {
      up.addEventListener("click", () => this._bumpField(which, 1))
      dn.addEventListener("click", () => this._bumpField(which, -1))
    } else {
      up.disabled = true
      dn.disabled = true
    }
    steppers.appendChild(up)
    steppers.appendChild(dn)
    box.appendChild(steppers)

    wrap.appendChild(box)
    this._stripFields[which] = { input }
    return wrap
  }

  _stepperBtn(d, withTopBorder) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className =
      "flex-1 w-6 grid place-items-center text-app-text-faint hover:text-app-text hover:bg-app-surface-hover" +
      (withTopBorder ? " border-t border-app-border-subtle" : "")
    const svg = svgEl("svg", {
      width: "11",
      height: "11",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "3",
    })
    svg.appendChild(svgEl("path", { d }))
    btn.appendChild(svg)
    return btn
  }

  // Update the strip field values in place WITHOUT rebuilding inputs. A focused
  // input is skipped so the user's in-progress typing is never clobbered.
  _updateStripValues(corner) {
    if (this._stripFields) {
      for (const which of ["start", "apex", "end"]) {
        const f = this._stripFields[which]
        if (!f) continue
        if (document.activeElement === f.input) continue // don't clobber typing
        f.input.value = String(Math.round(corner[`${which}_m`]))
      }
    }
    if (this._stripLength) {
      this._stripLength.textContent = `${Math.round(corner.end_m - corner.start_m)} m`
    }
    if (this._stripDirBtns) {
      for (const v of ["left", "right"]) {
        const btn = this._stripDirBtns[v]
        if (!btn) continue
        const on = corner.direction === v
        btn.style.background = on ? btn.dataset.hue || "" : ""
        btn.className =
          "w-9 rounded-md text-[12px] font-mono font-bold transition-colors " +
          (on
            ? "text-white"
            : "text-app-text-faint hover:text-app-text-secondary")
      }
    }
  }

  // Commit a raw input string for a field: parse, clamp, apply to the working
  // copy, persist the full window. NaN reverts to the current value. Clamp bounds
  // (per NumField): start [0, apex] · apex [start, end] · end [apex, length].
  _commitField(which, raw) {
    const corner =
      this._selectedNumber == null
        ? null
        : this._cornerByNumber(this._selectedNumber)
    if (!corner) return
    let n = parseInt(raw, 10)
    if (isNaN(n)) {
      // Revert the input to the current rounded value.
      const f = this._stripFields && this._stripFields[which]
      if (f) f.input.value = String(Math.round(corner[`${which}_m`]))
      return
    }
    n = this._clampFieldValue(corner, which, n)
    this._applyField(corner, which, n)
  }

  _bumpField(which, d) {
    const corner =
      this._selectedNumber == null
        ? null
        : this._cornerByNumber(this._selectedNumber)
    if (!corner) return
    const n = this._clampFieldValue(
      corner,
      which,
      Math.round(corner[`${which}_m`]) + d,
    )
    this._applyField(corner, which, n)
  }

  _clampFieldValue(corner, which, n) {
    if (which === "start") return Math.max(0, Math.min(corner.apex_m, n))
    if (which === "apex")
      return Math.max(corner.start_m, Math.min(corner.end_m, n))
    return Math.max(corner.apex_m, Math.min(this._L, n)) // end
  }

  // Write the edited field to the working copy, re-render (mirrors the handle),
  // and persist the full window. The card_stream re-sync settles handles +
  // waveform + strip to the server's committed geometry.
  _applyField(corner, which, value) {
    corner[`${which}_m`] = value
    corner.source = "manual"
    this._render()
    if (corner.id == null) return
    this._persistGeometry(corner.id, {
      start_m: corner.start_m,
      apex_m: corner.apex_m,
      end_m: corner.end_m,
    })
  }

  // Direction toggle → persist {direction}. Mirror into the working copy first so
  // the toggle reflects immediately; the card_stream re-sync settles it.
  _setDirection(corner, dir) {
    if (corner.direction === dir) return
    corner.direction = dir
    corner.source = "manual"
    this._render()
    if (corner.id == null) return
    this._persistAttrs(corner.id, { direction: dir })
  }

  // Per-corner Magic fit → PATCH …/corners/:id/magic. The server returns the
  // card_stream Turbo Stream; we render it, and the corner-curation parent's
  // reconnected map outlet re-syncs THIS timeline off the freshly-rendered,
  // late-apex-biased geometry. The apex should shift late.
  _magicFitCorner(id) {
    if (id == null) return
    const tmpl = this.magicUrlTemplateValue
    if (!tmpl) return
    const url = tmpl.replace("__ID__", String(id))
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(url, {
      method: "PATCH",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`magic PATCH ${res.status}`)
        return res.text()
      })
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        this._render()
      })
  }

  // ── Remove (styled confirm dialog) ─────────────────────────────────────────
  //
  // Remove is destructive, so it goes through the shared confirm-dialog <dialog>
  // (custom-confirm-dialog rule) — never native confirm(). The dialog markup +
  // confirm-dialog controller live in _track_detail.html.erb; here we open it,
  // fill the consequence copy, and on confirm fire a JS fetch(DELETE) → adopt.

  _openRemoveDialog(corner) {
    if (!this.hasRemoveDialogTarget) return
    this._pendingRemoveId = corner.id
    if (this.hasRemoveBodyTarget) {
      const named = corner.name && String(corner.name).trim()
      const label = named ? `“${corner.name}”` : `Turn ${corner.number}`
      this.removeBodyTarget.textContent = `${label} and its name will be permanently deleted, and the remaining corners renumbered. This can't be undone.`
    }
    const ctrl = this._removeDialogController()
    if (ctrl) ctrl.open()
    else this.removeDialogTarget.showModal()
  }

  // Confirm-remove handler (wired from the dialog's Remove button via
  // data-action="corner-timeline#confirmRemove"). Swaps to the spinner, fires the
  // DELETE, renders the returned card_stream Turbo Stream (corners-card + hero-map
  // replace), and deselects. The reconnected map outlet re-syncs THIS timeline off
  // the freshly-rendered, renumbered geometry; clearing selection here ensures a
  // removed corner can't stay lit.
  confirmRemove() {
    const id = this._pendingRemoveId
    if (id == null) return
    const tmpl = this.removeUrlTemplateValue
    if (!tmpl) return
    const url = tmpl.replace("__ID__", String(id))
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    const ctrl = this._removeDialogController()
    if (ctrl) ctrl.submit() // swap the buttons row for the progress spinner

    fetch(url, {
      method: "DELETE",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`remove DELETE ${res.status}`)
        return res.text()
      })
      .then((html) => {
        this._closeRemoveDialog()
        this._pendingRemoveId = null
        this._selectedNumber = null
        this.dispatch("cornerSelect", {
          detail: { number: null },
          bubbles: true,
        })
        window.Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        // Leave the dialog; reset the spinner so the user can retry.
        this._closeRemoveDialog()
      })
  }

  _removeDialogController() {
    if (!this.hasRemoveDialogTarget) return null
    const app = this.application
    if (!app || typeof app.getControllerForElementAndIdentifier !== "function")
      return null
    return app.getControllerForElementAndIdentifier(
      this.removeDialogTarget,
      "confirm-dialog",
    )
  }

  _closeRemoveDialog() {
    if (!this.hasRemoveDialogTarget) return
    const ctrl = this._removeDialogController()
    // Reset the confirm-dialog progress state so a re-open shows buttons again.
    if (ctrl) {
      ctrl.deleting = false
      if (ctrl.hasButtonsTarget) ctrl.buttonsTarget.classList.remove("hidden")
      if (ctrl.hasProgressTarget) ctrl.progressTarget.classList.add("hidden")
    }
    if (this.removeDialogTarget.open) this.removeDialogTarget.close()
  }

  // PATCH arbitrary attrs (e.g. {direction}) to #geometry. Shares the geometry
  // endpoint + card_stream re-sync path with _persistGeometry; kept DRY by
  // delegating both to one fetch helper.
  _persistAttrs(id, attrs) {
    this._patchGeometry(id, attrs)
  }

  // Deep clone the corners array into a stable, mutable working copy. Used to
  // seed `this._corners` from the Stimulus value (which re-parses on every read).
  _cloneCorners(arr) {
    return this._cloneArray(arr)
  }

  // Deep clone an array into a stable, mutable working copy. Used for both the
  // corners and sections working copies (Stimulus values re-parse on every read).
  _cloneArray(arr) {
    const a = Array.isArray(arr) ? arr : []
    return typeof structuredClone === "function"
      ? structuredClone(a)
      : JSON.parse(JSON.stringify(a))
  }

  // ── Persistence (PATCH on release) ─────────────────────────────────────────

  // PATCH the dragged corner's window to the server. On success the server
  // returns the card_stream Turbo Stream; rendering it re-creates the hero map,
  // whose outlet reconnect re-syncs this timeline off the server's renumbered
  // geometry. On failure, revert: the value attribute was never written during
  // the drag, so re-seeding the working copy from it snaps the span back.
  _persistGeometry(id, window) {
    this._patchGeometry(id, window)
  }

  // Shared #geometry PATCH used by both the handle/field geometry commit
  // (start/apex/end) and the direction toggle ({direction}). On success the
  // server returns the card_stream Turbo Stream (corners-card + hero-map
  // replace); we render it, which re-creates the hero map → the corner-curation
  // parent's map outlet reconnects and re-syncs THIS timeline off the server's
  // freshly-rendered, renumbered geometry (preserving zoom + selection). On
  // failure we re-seed the working copy from the un-mutated value so the edit
  // snaps back.
  _patchGeometry(id, attrs) {
    const tmpl = this.geometryUrlTemplateValue
    if (!tmpl) {
      this._render()
      return
    }
    const url = tmpl.replace("__ID__", String(id))
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify(attrs),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`geometry PATCH ${res.status}`)
        return res.text()
      })
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        // Revert: re-seed the working copy from the last known-good server value
        // (the un-mutated attribute) and re-render so the edit snaps back.
        this._corners = this._cloneCorners(this.cornersGeometryValue)
        this._render()
      })
  }

  // ── Section bands overlay (Phase C) ─────────────────────────────────────────
  //
  // In Sections mode the lap is drawn as contiguous tiling bands inside the SAME
  // `plot` host as the waveform, sharing `_xOf`/`_view`/zoom. Ported from
  // SectionBands (design/cw-section-ui.jsx):
  //   • each [start_m,end_m] is a palette-colour band with a coloured title rail
  //     (number + name, ellipsis below ~54px) and rounded first/last outer corners
  //   • the shared boundary between band i and i+1 is a full-height ew-resize
  //     handle with a two-colour seam, a grab pill (⇿ chevrons), and a live
  //     `#### m` distance bubble while active
  //   • ghosted corner diamonds along the bottom (context, from `_corners`)
  //   • dashed S/F caps at lap start + end
  _buildBands(plot) {
    const layer = document.createElement("div")
    layer.className = "absolute inset-0"
    layer.style.height = `${WAVE_H}px`
    layer.style.pointerEvents = "none" // children opt back in

    const W = this._W
    const h = WAVE_H
    const sections = Array.isArray(this._sections) ? this._sections : []
    const corners = Array.isArray(this._corners) ? this._corners : []
    const activeB = this._activeBoundary
    const fmt = (m) => Math.round(m).toLocaleString()

    // Ghosted corner diamonds underneath (context: boundaries vs corners).
    for (const c of corners) {
      const x = this._xOf(c.apex_m)
      if (x < -8 || x > W + 8) continue
      const d = document.createElement("div")
      d.style.cssText =
        `position:absolute;left:${x}px;top:${h - 15}px;transform:translateX(-50%);` +
        `width:6px;height:6px;background:var(--app-text-faint);opacity:0.5;pointer-events:none`
      d.style.transform = "translateX(-50%) rotate(45deg)"
      d.title = c.name && String(c.name).trim() ? c.name : `Turn ${c.number}`
      layer.appendChild(d)
    }

    // Tiling bands. Keep refs to the band nodes so hover/select can update their
    // emphasis IN PLACE (see _applySectionEmphasis) rather than rebuilding the
    // overlay — rebuilding on hover swapped the click target out from under the
    // pointer, which is why real click-select and double-click-split never fired.
    this._sectionBandEls = []
    sections.forEach((s, i) => {
      const x0 = this._xOf(s.start_m),
        x1 = this._xOf(s.end_m),
        w = x1 - x0
      if (x1 < -4 || x0 > W + 4) return
      const col = sectionColorFor(i)
      const sel = String(s.id) === String(this._selectedSectionId)
      // Band hover (from this surface or routed in via setHoveredSection) is
      // distinct from boundary-handle hover (`_activeBoundary` / `adj`); a hovered
      // band sits between idle and selected emphasis. Selection wins over hover.
      const hov = !sel && String(s.id) === String(this._hoveredSectionId)
      // A band lights when a boundary on EITHER side is active.
      const adj = activeB === i || activeB === i - 1
      const lit = sel || adj || hov
      const named = s.name && String(s.name).trim()

      const band = document.createElement("div")
      // Emphasis ordering: idle 0.1 < hover 0.18 < adj 0.22 < selected 0.28.
      const bandOpacity = sel ? 0.28 : adj ? 0.22 : hov ? 0.18 : 0.1
      band.style.cssText =
        `position:absolute;left:${x0}px;width:${Math.max(2, w)}px;top:0;height:${h}px;` +
        `cursor:pointer;background:${col};opacity:${bandOpacity};` +
        `transition:opacity .12s;pointer-events:auto;z-index:4`
      const sid = String(s.id)
      band.title = "Click to select · double-click to split"
      // Select on click, but DELAY it so a double-click can pre-empt the select
      // (clearing the timer) and split instead. Selection state is read fresh
      // from `this._selectedSectionId` — not a render-time `sel` closure — because
      // the band node now outlives a selection (we no longer rebuild on select).
      band.addEventListener("click", (e) => {
        e.stopPropagation()
        clearTimeout(this._sectionClickTimer)
        this._sectionClickTimer = setTimeout(() => {
          const isSel = sid === String(this._selectedSectionId)
          this._onSectionSelect(isSel ? null : sid)
        }, 180)
      })
      band.addEventListener("dblclick", (e) => {
        e.stopPropagation()
        clearTimeout(this._sectionClickTimer)
        const r = plot.getBoundingClientRect()
        this._splitSectionAt(this._mOf(e.clientX - r.left))
      })
      band.addEventListener("mouseenter", () => {
        this._hoveredSectionId = sid
        this._applySectionEmphasis()
        this.dispatch("sectionHover", { detail: { id: sid }, bubbles: true })
      })
      band.addEventListener("mouseleave", () => {
        if (this._hoveredSectionId === sid) this._hoveredSectionId = null
        this._applySectionEmphasis()
        this.dispatch("sectionLeave", { detail: { id: sid }, bubbles: true })
      })

      // Title rail.
      const rail = document.createElement("div")
      rail.style.cssText =
        `position:absolute;left:0;right:0;top:0;height:22px;background:${col};` +
        `opacity:${lit ? 1 : 0.62};display:flex;align-items:center;gap:5px;padding:0 7px;` +
        `overflow:hidden;white-space:nowrap;transition:opacity .12s;pointer-events:none;` +
        `border-top-left-radius:${i === 0 ? 5 : 0}px;border-top-right-radius:${i === sections.length - 1 ? 5 : 0}px`
      const numSpan = document.createElement("span")
      numSpan.style.cssText =
        "font-family:'JetBrains Mono',monospace;font-size:9.5px;font-weight:700;color:#fff;opacity:0.92"
      numSpan.textContent = s.number
      rail.appendChild(numSpan)
      if (w > 54) {
        const nameSpan = document.createElement("span")
        nameSpan.style.cssText =
          "font-size:10.5px;font-weight:600;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.45);overflow:hidden;text-overflow:ellipsis"
        nameSpan.textContent = named ? s.name : `Section ${s.number}`
        rail.appendChild(nameSpan)
      }
      // Live length while a touching boundary is dragged.
      if (adj && w > 70) {
        const lenSpan = document.createElement("span")
        lenSpan.style.cssText =
          "margin-left:auto;font-family:'JetBrains Mono',monospace;font-size:9.5px;font-weight:700;color:#fff;opacity:0.95"
        lenSpan.textContent = `${fmt(s.end_m - s.start_m)}m`
        rail.appendChild(lenSpan)
      }
      band.appendChild(rail)

      if (sel) band.appendChild(this._sectionSelRing(col))
      layer.appendChild(band)
      this._sectionBandEls.push({ id: sid, i, col, band, rail })
    })

    // S/F continuity caps (first start + last end are the same lap point).
    const caps = sections.length
      ? [sections[0].start_m, sections[sections.length - 1].end_m]
      : [0, 0]
    for (const m of caps) {
      const x = this._xOf(m)
      if (x < -6 || x > W + 6) continue
      const cap = document.createElement("div")
      cap.style.cssText =
        `position:absolute;left:${x}px;top:0;height:${h}px;width:2px;transform:translateX(-50%);` +
        `pointer-events:none;opacity:0.6;background:repeating-linear-gradient(to bottom, var(--app-text-faint) 0 4px, transparent 4px 8px)`
      layer.appendChild(cap)
    }

    // Shared boundary handles (between section i and i+1).
    for (let i = 0; i < sections.length - 1; i++) {
      const s = sections[i]
      const x = this._xOf(s.end_m)
      if (x < -10 || x > W + 10) continue
      const on = activeB === i
      const lcol = sectionColorFor(i),
        rcol = sectionColorFor(i + 1)

      const wrap = document.createElement("div")
      wrap.style.cssText =
        `position:absolute;left:${x}px;top:0;height:${h}px;width:18px;transform:translateX(-50%);` +
        `cursor:ew-resize;z-index:${on ? 30 : 22};touch-action:none;pointer-events:auto`
      wrap.title = `Boundary · ${sectionDisplayName(sections[i])} ⇄ ${sectionDisplayName(sections[i + 1])}`
      wrap.addEventListener("mouseenter", () => {
        if (this._boundaryDrag) return
        this._activeBoundary = i
        // Entering a boundary handle supersedes any band hover (design parity);
        // clear it everywhere so the two emphases don't stack.
        if (this._hoveredSectionId != null) {
          this._hoveredSectionId = null
          this.dispatch("sectionLeave", { detail: { id: null }, bubbles: true })
        }
        this._render()
      })
      wrap.addEventListener("mouseleave", () => {
        if (!this._boundaryDrag && this._activeBoundary === i) {
          this._activeBoundary = null
          this._render()
        }
      })
      wrap.addEventListener("pointerdown", (e) => {
        e.stopPropagation()
        this._startBoundaryDrag(e, i)
      })

      // Two-colour seam (top half = left section, bottom = right).
      const top = document.createElement("div")
      top.style.cssText =
        `position:absolute;left:50%;top:0;height:50%;width:${on ? 3 : 2}px;` +
        `transform:translateX(-50%);background:${lcol};opacity:${on ? 1 : 0.7};transition:all .1s`
      wrap.appendChild(top)
      const bot = document.createElement("div")
      bot.style.cssText =
        `position:absolute;left:50%;top:50%;height:50%;width:${on ? 3 : 2}px;` +
        `transform:translateX(-50%);background:${rcol};opacity:${on ? 1 : 0.7};transition:all .1s`
      wrap.appendChild(bot)

      // Live distance bubble while active.
      if (on) {
        const bubble = document.createElement("div")
        bubble.style.cssText =
          "position:absolute;left:50%;top:26px;transform:translateX(-50%);padding:2px 6px;" +
          "border-radius:5px;background:var(--vp);color:#fff;font-family:'JetBrains Mono',monospace;" +
          "font-size:10px;font-weight:700;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.35)"
        bubble.textContent = `${fmt(s.end_m)} m`
        wrap.appendChild(bubble)
      }

      // Grab capsule — two opposing chevrons pointing into each neighbour.
      const pill = document.createElement("div")
      pill.style.cssText =
        `position:absolute;left:50%;top:${h / 2 - 15}px;transform:translateX(-50%);` +
        `width:${on ? 22 : 16}px;height:30px;border-radius:6px;background:var(--app-surface);` +
        `border:1.5px solid ${on ? "var(--vp)" : "var(--app-border-strong)"};` +
        `box-shadow:${on ? "0 3px 10px rgba(0,0,0,.4)" : "0 1px 4px rgba(0,0,0,.3)"};` +
        `display:flex;align-items:center;justify-content:center;gap:1px;transition:all .1s`
      const lChev = svgEl("svg", {
        width: "6",
        height: "12",
        viewBox: "0 0 6 12",
        fill: "none",
      })
      lChev.appendChild(
        svgEl("path", {
          d: "M5 2L1 6l4 4",
          stroke: on ? lcol : "var(--app-text-muted)",
          "stroke-width": "1.6",
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
        }),
      )
      pill.appendChild(lChev)
      const rChev = svgEl("svg", {
        width: "6",
        height: "12",
        viewBox: "0 0 6 12",
        fill: "none",
      })
      rChev.appendChild(
        svgEl("path", {
          d: "M1 2l4 4-4 4",
          stroke: on ? rcol : "var(--app-text-muted)",
          "stroke-width": "1.6",
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
        }),
      )
      pill.appendChild(rChev)
      wrap.appendChild(pill)

      layer.appendChild(wrap)
    }

    return layer
  }

  // ── Boundary drag (the core interaction) ────────────────────────────────────
  //
  // Drag the shared boundary between section i and i+1. Window pointermove/up
  // (decoupled from the DOM so mid-drag re-renders are safe). Each move updates
  // `_sections` via moveBoundary (MIN 60 clamp), re-renders so BOTH neighbours
  // rebalance live, and emits sectionEdit for the map preview (Phase E/F). On
  // release: PATCH …/sections/:id/boundary {index:i, m:<new boundary>} using
  // section[i].id; the server MoveBoundary re-clamps + renumbers, returns the
  // card_stream, and the parent re-syncs this timeline off the committed state.
  _startBoundaryDrag(e, i) {
    e.preventDefault()
    if (!this.hasHostTarget) return
    const plot = this.hostTarget.querySelector("[data-cw-plot]")
    const rect = (plot || this.hostTarget).getBoundingClientRect()
    this._boundaryDrag = { i, rect, view: { a: this._view.a, b: this._view.b } }
    this._activeBoundary = i

    const move = (ev) => this._onBoundaryMove(ev)
    const up = () => this._onBoundaryUp()
    this._boundaryMove = move
    this._boundaryUp = up
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  _onBoundaryMove(ev) {
    const d = this._boundaryDrag
    if (!d) return
    const m = this._mOfAt(ev.clientX - d.rect.left, d.view)
    this._sections = moveBoundary(this._sections, d.i, m)
    this._render()
    this.dispatch("sectionEdit", {
      detail: { sections: this._sections.map((s) => ({ ...s })) },
      bubbles: true,
    })
  }

  _onBoundaryUp() {
    const d = this._boundaryDrag
    this._teardownBoundaryDrag()
    if (!d) return
    const left = this._sections[d.i]
    if (!left || left.id == null) {
      this._render()
      return
    }
    const tmpl = this.boundaryUrlTemplateValue
    if (!tmpl) {
      this._render()
      return
    }
    const url = tmpl.replace("__ID__", String(left.id))
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ index: d.i, m: Math.round(left.end_m) }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`boundary PATCH ${res.status}`)
        return res.text()
      })
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        this._render()
      })
  }

  _teardownBoundaryDrag() {
    if (this._boundaryMove) {
      window.removeEventListener("pointermove", this._boundaryMove)
      this._boundaryMove = null
    }
    if (this._boundaryUp) {
      window.removeEventListener("pointerup", this._boundaryUp)
      this._boundaryUp = null
    }
    this._boundaryDrag = null
    this._activeBoundary = null
  }

  // Double-click a band → split the containing section at the click metre. POST
  // …/sections/split {at_m}; the server SplitAt clamps + renumbers + re-renders.
  _splitSectionAt(m) {
    const url = this.splitUrlValue
    if (!url) return
    const i = sectionIndexAt(this._sections, m)
    if (i < 0) return
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ at_m: Math.round(m) }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`split POST ${res.status}`)
        return res.text()
      })
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        this._render()
      })
  }

  // ── Section strip (Phase C) ─────────────────────────────────────────────────
  //
  // The docked strip in Sections mode. Ported from SectionStrip (cw-section-ui.jsx):
  // selected section → colour+number+name · numeric Start (edits the boundary with
  // the PREVIOUS section; disabled for the first) · End (edits the boundary with
  // the NEXT; disabled for the last) · Length · contained-corner chips · Merge
  // (danger, opens the confirm-dialog). Empty (no selection) → a hint row.
  //
  // Focus-safe in-place update, like the corner geometry strip: the strip DOM is
  // rebuilt only when the selection identity (_secStripKey) changes; otherwise the
  // Start/End values + Length update in place so a boundary drag (or typing) never
  // clobbers a focused field.
  _renderSectionStrip() {
    const strip = this.stripTarget
    // Switching from the corner strip → force a clean rebuild next corner select.
    this._stripKey = undefined

    const section =
      this._selectedSectionId == null
        ? null
        : this._sections.find(
            (s) => String(s.id) === String(this._selectedSectionId),
          )

    const key = section ? `sec:${section.id}` : "sec:none"
    if (key !== this._secStripKey) {
      this._secStripKey = key
      this._secStripFields = null
      this._secStripLength = null
      strip.innerHTML = ""
      strip.className = section
        ? "shrink-0 border-t border-app-border bg-app-surface-alt"
        : "shrink-0 border-t border-app-border bg-app-surface-alt"
      strip.appendChild(
        section
          ? this._buildSectionStrip(section)
          : this._buildSectionStripHint(),
      )
      return
    }
    if (section) this._updateSectionStripValues(section)
  }

  _buildSectionStripHint() {
    const row = document.createElement("div")
    row.className = "flex items-center gap-2 px-5 h-[58px]"
    const svg = svgEl("svg", {
      width: "15",
      height: "15",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.7",
      class: "text-app-text-faint shrink-0",
    })
    svg.appendChild(
      svgEl("rect", { x: "3", y: "9", width: "18", height: "6", rx: "1" }),
    )
    svg.appendChild(svgEl("path", { d: "M9 9v6M15 9v6" }))
    row.appendChild(svg)
    const span = document.createElement("span")
    span.className = "text-[12.5px] text-app-text-muted"
    span.appendChild(document.createTextNode("Select a section, or "))
    const b1 = document.createElement("b")
    b1.className = "text-app-text"
    b1.textContent = "drag a boundary"
    span.appendChild(b1)
    span.appendChild(document.createTextNode(" to move it · "))
    const b2 = document.createElement("b")
    b2.className = "text-app-text"
    b2.textContent = "double-click a band"
    span.appendChild(b2)
    span.appendChild(document.createTextNode(" to split."))
    row.appendChild(span)
    return row
  }

  _buildSectionStrip(section) {
    const sections = this._sections
    const i = sections.findIndex((s) => String(s.id) === String(section.id))
    const corners = Array.isArray(this._corners) ? this._corners : []
    const inside = containedCorners(section, corners)
    const len = Math.round(section.end_m - section.start_m)
    const named = section.name && String(section.name).trim()
    const col = sectionColorFor(i)

    // Same row container as the corner geometry strip (_buildStripSelected) so
    // the two strips — which swap into the SAME docked slot on the mode toggle —
    // are identical height with their value boxes on the same baseline. py-3.5
    // gives the breathing room below the inputs; h-9 fields match the corner
    // strip. Without this the section strip was shorter (h-[58px]) with h-8
    // boxes pinned flush to the bottom, so toggling modes jumped the inputs.
    const row = document.createElement("div")
    row.className = "flex items-end gap-x-4 gap-y-3 flex-wrap px-5 py-3.5"

    // Number badge + name. h-9 (matching the Start/End input boxes and the
    // corner strip's chip row) so its content centres on the shared baseline.
    const idWrap = document.createElement("div")
    idWrap.className = "flex items-center gap-2 h-9"
    const badge = document.createElement("span")
    badge.className =
      "w-6 h-6 grid place-items-center rounded-md font-mono text-[11px] font-bold text-white"
    badge.style.background = col
    badge.textContent = section.number
    idWrap.appendChild(badge)
    const nameSpan = document.createElement("span")
    nameSpan.className = named
      ? "text-[13.5px] font-semibold text-app-text"
      : "text-[13.5px] italic text-app-text-faint"
    nameSpan.textContent = named ? section.name : `Section ${section.number}`
    idWrap.appendChild(nameSpan)
    row.appendChild(idWrap)

    // Start (edits boundary i-1) / End (edits boundary i).
    this._secStripFields = {}
    row.appendChild(
      this._buildSectionNumField("start", "Start", section.start_m, i === 0),
    )
    row.appendChild(
      this._buildSectionNumField(
        "end",
        "End",
        section.end_m,
        i === sections.length - 1,
      ),
    )

    // Length.
    const lenWrap = document.createElement("div")
    lenWrap.className = "flex flex-col gap-1"
    const lenLab = document.createElement("span")
    lenLab.className =
      "text-[10px] font-mono uppercase tracking-wider text-app-text-faint"
    lenLab.textContent = "Length"
    lenWrap.appendChild(lenLab)
    const lenVal = document.createElement("span")
    lenVal.className =
      "font-mono text-[13px] font-semibold text-app-text tabular-nums h-9 flex items-center"
    lenVal.textContent = `${(len / 1000).toFixed(2)} km`
    this._secStripLength = lenVal
    lenWrap.appendChild(lenVal)
    row.appendChild(lenWrap)

    // Contained-corner chips.
    const cornWrap = document.createElement("div")
    cornWrap.className = "flex flex-col gap-1 min-w-0"
    const cornLab = document.createElement("span")
    cornLab.className =
      "text-[10px] font-mono uppercase tracking-wider text-app-text-faint"
    cornLab.textContent = "Corners"
    cornWrap.appendChild(cornLab)
    const chips = document.createElement("div")
    chips.className = "h-9 flex items-center gap-1 overflow-hidden"
    if (inside.length === 0) {
      const none = document.createElement("span")
      none.className = "text-[12px] text-app-text-faint italic"
      none.textContent = "none"
      chips.appendChild(none)
    } else {
      for (const c of inside) {
        const chip = document.createElement("span")
        chip.className =
          "shrink-0 px-1.5 h-[19px] inline-flex items-center rounded bg-app-surface-active/60 text-app-text-secondary font-mono text-[10px] font-semibold"
        chip.title =
          c.name && String(c.name).trim() ? c.name : `Turn ${c.number}`
        chip.textContent = c.number
        chips.appendChild(chip)
      }
    }
    cornWrap.appendChild(chips)
    row.appendChild(cornWrap)

    // Merge (danger) — opens the confirm-dialog.
    const mergeBtn = document.createElement("button")
    mergeBtn.type = "button"
    mergeBtn.className = "apx-btn apx-btn--danger apx-btn--sm ml-auto mb-0.5"
    mergeBtn.disabled = sections.length <= 1
    if (sections.length <= 1) mergeBtn.style.opacity = "0.4"
    const mSvg = svgEl("svg", {
      width: "14",
      height: "14",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.7",
    })
    mSvg.appendChild(
      svgEl("path", {
        "stroke-linecap": "round",
        d: "M8 7l-4 5 4 5M16 7l4 5-4 5",
      }),
    )
    mergeBtn.appendChild(mSvg)
    mergeBtn.appendChild(document.createTextNode("Merge"))
    mergeBtn.addEventListener("click", () => {
      if (sections.length > 1) this._openMergeDialog(section, i)
    })
    row.appendChild(mergeBtn)

    return row
  }

  // Numeric Start/End field. Start edits the boundary with the previous section
  // (index i-1); End edits the boundary with the next (index i). Commit clamps via
  // moveBoundary then PATCHes, mirroring a boundary drag's release.
  _buildSectionNumField(which, label, value, disabled) {
    const wrap = document.createElement("label")
    wrap.className = "flex flex-col gap-1" + (disabled ? " opacity-45" : "")
    const lab = document.createElement("span")
    lab.className =
      "text-[10px] font-mono uppercase tracking-wider text-app-text-faint"
    lab.textContent = label
    wrap.appendChild(lab)
    const box = document.createElement("div")
    box.className =
      "inline-flex items-center h-9 rounded-lg bg-app-input-bg ring-1 ring-app-input-ring focus-within:ring-2 focus-within:ring-vivid-purple overflow-hidden"
    const input = document.createElement("input")
    input.type = "text"
    input.inputMode = "numeric"
    input.className =
      "w-[68px] h-full bg-transparent px-2.5 text-[12.5px] font-mono tabular-nums text-app-text outline-none disabled:cursor-not-allowed"
    input.value = String(Math.round(value))
    input.disabled = disabled
    if (!disabled) {
      input.addEventListener("input", () => {
        input.value = input.value.replace(/[^0-9]/g, "")
      })
      input.addEventListener("blur", () =>
        this._commitSectionField(which, input.value),
      )
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") input.blur()
      })
    }
    box.appendChild(input)
    const unit = document.createElement("span")
    unit.className = "text-[10.5px] font-mono text-app-text-faint pr-2"
    unit.textContent = "m"
    box.appendChild(unit)
    wrap.appendChild(box)
    this._secStripFields[which] = { input }
    return wrap
  }

  // Commit a Start/End edit → translate to a boundary move + PATCH. Start moves
  // boundary i-1 (with the previous section); End moves boundary i (with the next).
  _commitSectionField(which, raw) {
    const section =
      this._selectedSectionId == null
        ? null
        : this._sections.find(
            (s) => String(s.id) === String(this._selectedSectionId),
          )
    if (!section) return
    const i = this._sections.findIndex(
      (s) => String(s.id) === String(section.id),
    )
    const n = parseInt(raw, 10)
    if (isNaN(n)) {
      // Revert to the current value.
      const f = this._secStripFields && this._secStripFields[which]
      if (f)
        f.input.value = String(
          Math.round(which === "start" ? section.start_m : section.end_m),
        )
      return
    }
    const boundaryIndex = which === "start" ? i - 1 : i
    if (boundaryIndex < 0 || boundaryIndex >= this._sections.length - 1) return
    this._sections = moveBoundary(this._sections, boundaryIndex, n)
    this._render()
    this._commitBoundary(boundaryIndex)
  }

  // PATCH a boundary to the server off the current working copy (shared by the
  // Start/End fields). Mirrors _onBoundaryUp's request.
  _commitBoundary(index) {
    const left = this._sections[index]
    if (!left || left.id == null) return
    const tmpl = this.boundaryUrlTemplateValue
    if (!tmpl) return
    const url = tmpl.replace("__ID__", String(left.id))
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ index, m: Math.round(left.end_m) }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`boundary PATCH ${res.status}`)
        return res.text()
      })
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        this._render()
      })
  }

  _updateSectionStripValues(section) {
    if (this._secStripFields) {
      const map = { start: section.start_m, end: section.end_m }
      for (const which of ["start", "end"]) {
        const f = this._secStripFields[which]
        if (!f) continue
        if (document.activeElement === f.input) continue // don't clobber typing
        f.input.value = String(Math.round(map[which]))
      }
    }
    if (this._secStripLength) {
      this._secStripLength.textContent = `${((section.end_m - section.start_m) / 1000).toFixed(2)} km`
    }
  }

  // ── Section merge (styled confirm dialog) ───────────────────────────────────
  //
  // Merge is destructive (collapses a section into a neighbour), so it goes
  // through the shared confirm-dialog <dialog> — never native confirm(). We merge
  // toward the PREVIOUS neighbour (dir -1) when one exists, else the next (+1).

  _openMergeDialog(section, i) {
    if (!this.hasMergeDialogTarget) return
    this._pendingMergeId = section.id
    // Prefer merging into the previous section; the first section folds forward.
    const dir = i > 0 ? -1 : 1
    this._pendingMergeDir = dir
    const neighbour = this._sections[i + dir]
    if (this.hasMergeBodyTarget) {
      const label = sectionDisplayName(section)
      const into = neighbour ? sectionDisplayName(neighbour) : "its neighbour"
      this.mergeBodyTarget.textContent = `${label} will be merged into ${into} and the remaining sections renumbered. This can't be undone.`
    }
    const ctrl = this._mergeDialogController()
    if (ctrl) ctrl.open()
    else this.mergeDialogTarget.showModal()
  }

  // Wired from the dialog's Merge button via data-action="corner-timeline#confirmMerge".
  confirmMerge() {
    const id = this._pendingMergeId
    if (id == null) return
    const tmpl = this.mergeUrlTemplateValue
    if (!tmpl) return
    const url = tmpl.replace("__ID__", String(id))
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    const ctrl = this._mergeDialogController()
    if (ctrl) ctrl.submit() // swap buttons for the progress spinner

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ dir: this._pendingMergeDir }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`merge POST ${res.status}`)
        return res.text()
      })
      .then((html) => {
        this._closeMergeDialog()
        this._pendingMergeId = null
        this._selectedSectionId = null
        this.dispatch("sectionSelect", { detail: { id: null }, bubbles: true })
        window.Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        this._closeMergeDialog()
      })
  }

  _mergeDialogController() {
    if (!this.hasMergeDialogTarget) return null
    const app = this.application
    if (!app || typeof app.getControllerForElementAndIdentifier !== "function")
      return null
    return app.getControllerForElementAndIdentifier(
      this.mergeDialogTarget,
      "confirm-dialog",
    )
  }

  _closeMergeDialog() {
    if (!this.hasMergeDialogTarget) return
    const ctrl = this._mergeDialogController()
    if (ctrl) {
      ctrl.deleting = false
      if (ctrl.hasButtonsTarget) ctrl.buttonsTarget.classList.remove("hidden")
      if (ctrl.hasProgressTarget) ctrl.progressTarget.classList.add("hidden")
    }
    if (this.mergeDialogTarget.open) this.mergeDialogTarget.close()
  }

  // ── Section select wiring ───────────────────────────────────────────────────
  // (Cross-surface section hover live-link is Phase F; today only band SELECT and
  // boundary-handle hover drive band emphasis.)

  _onSectionSelect(id) {
    const next = id == null ? null : String(id)
    this._selectedSectionId = next
    // In place — keep the band nodes alive for click/dblclick. The strip is a
    // separate subtree, so rebuilding it doesn't disturb the bands.
    this._applySectionEmphasis()
    this._renderSectionStrip()
    this.dispatch("sectionSelect", { detail: { id: next }, bubbles: true })
  }

  _sectionSelRing(col) {
    const ring = document.createElement("div")
    ring.setAttribute("data-sel-ring", "")
    ring.style.cssText = `position:absolute;inset:0;box-shadow:inset 0 0 0 1.5px ${col};pointer-events:none`
    return ring
  }

  // Recompute each section band's emphasis (fill opacity · rail opacity ·
  // selection ring) from the current selected / hovered / active-boundary state,
  // mutating the existing band nodes IN PLACE. Used by hover and select so the
  // band DOM survives the pointer interaction — a full _render() would swap the
  // nodes out mid-click and break click/dblclick. No-op before the bands exist.
  _applySectionEmphasis() {
    const els = this._sectionBandEls
    if (!els || !els.length) return
    const activeB = this._activeBoundary
    for (const { id, i, col, band, rail } of els) {
      const sel = id === String(this._selectedSectionId)
      const hov = !sel && id === String(this._hoveredSectionId)
      const adj = activeB === i || activeB === i - 1
      band.style.opacity = sel ? "0.28" : adj ? "0.22" : hov ? "0.18" : "0.1"
      rail.style.opacity = sel || adj || hov ? "1" : "0.62"
      const ring = band.querySelector("[data-sel-ring]")
      if (sel && !ring) band.appendChild(this._sectionSelRing(col))
      else if (!sel && ring) ring.remove()
    }
  }

  // ── Hover / select wiring ──────────────────────────────────────────────────

  _onCornerHover(number) {
    if (this._drag) return // don't fight an active drag
    const n = number == null ? null : Number(number)
    if (this._hoveredNumber === n) return
    this._hoveredNumber = n
    this._render()
    this.dispatch("cornerHover", { detail: { number: n }, bubbles: true })
  }

  _onCornerSelect(number) {
    const n = number == null ? null : Number(number)
    this._selectedNumber = n
    this._render()
    this.dispatch("cornerSelect", { detail: { number: n }, bubbles: true })
  }

  // ── Events ───────────────────────────────────────────────────────────────

  _emitViewChange() {
    this.dispatch("viewChange", {
      detail: { view: { a: this._view.a, b: this._view.b } },
      bubbles: true,
    })
  }
}
